/**
 * Complete mapLayerUtils.js with enhanced renderer type normalization
 * Creates comparison map layers with improved label handling and robust type support
 */
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Point from "@arcgis/core/geometry/Point";
import Multipoint from "@arcgis/core/geometry/Multipoint";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Color from "@arcgis/core/Color";
import Graphic from "@arcgis/core/Graphic";
import PopupTemplate from "@arcgis/core/PopupTemplate";

// Import new coordinate-based label system
import CoordinateLabelManager from "../../services/CoordinateLabelManager";
import CoordinateLabelDragger from "../../services/CoordinateLabelDragger";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Polygon from "@arcgis/core/geometry/Polygon";
import Extent from "@arcgis/core/geometry/Extent";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import ClassBreaksRenderer from "@arcgis/core/renderers/ClassBreaksRenderer";
import DotDensityRenderer from "@arcgis/core/renderers/DotDensityRenderer";
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";

// Import color scheme for class breaks
import { createClassBreaks } from "./mapConfig";

// Import field formatting functions for comp maps
import { getCompFieldFormat } from "./heatMapGenerator";

// Import FEATURE_LAYERS for area type URL lookup
import { FEATURE_LAYERS } from "../../contexts/MapContext";

/**
 * Utility function to normalize renderer types with comprehensive fallback detection
 * Handles various type formats and normalizes them to expected renderer types
 * 
 * @param {string} type - The input type to normalize
 * @param {Object} config - Configuration object for auto-detection fallbacks
 * @returns {string|null} - Normalized type or null if unable to determine
 */
const normalizeRendererType = (type, config = {}) => {
  if (!type) {
    // Auto-detect type from config properties if type is missing
    if (config.classBreakInfos && Array.isArray(config.classBreakInfos)) {
      return 'class-breaks';
    } else if (config.attributes && Array.isArray(config.attributes)) {
      return 'dot-density';
    } else if (config.dotValue !== undefined || config.dotSize !== undefined) {
      return 'dot-density';
    }
    return null;
  }
  
  const typeStr = String(type).toLowerCase().trim();
  
  // Normalize heatmap and class-breaks variations
  if (typeStr === 'heatmap' || 
      typeStr === 'heat-map' || 
      typeStr === 'heat_map' ||
      typeStr === 'classbreaks' || 
      typeStr === 'class_breaks' ||
      typeStr === 'choropleth') {
    return 'class-breaks';
  }
  
  // Normalize dot density variations
  if (typeStr === 'dotdensity' || 
      typeStr === 'dot_density' || 
      typeStr === 'dot-density-map' ||
      typeStr === 'dotdensitymap' ||
      typeStr === 'dots') {
    return 'dot-density';
  }
  
  // Return normalized types as-is
  if (typeStr === 'class-breaks' || typeStr === 'dot-density') {
    return typeStr;
  }
  
  // Handle field-based type detection for _HEAT fields
  if (config.field && String(config.field).toUpperCase().endsWith('_HEAT')) {
    return 'class-breaks';
  }
  
  // Final fallback based on config properties
  if (config.classBreakInfos && Array.isArray(config.classBreakInfos)) {
    return 'class-breaks';
  } else if (config.attributes && Array.isArray(config.attributes) || config.dotValue !== undefined) {
    return 'dot-density';
  }
  
  // Return original type if no normalization possible
  return typeStr;
};

/**
 * Utility function to create default class break symbols using TCG color palette
 * 
 * @param {number} breakCount - Number of breaks to create colors for
 * @returns {Array} - Array of RGBA color arrays
 */
const createDefaultClassBreakSymbols = (breakCount) => {
  // TCG color palette for consistent styling
  // IMPORTANT: Colors go from Purple/Blue (lowest values) to Red (highest values)
  // Red is ALWAYS the last/highest value - no Pink/Brown/Grey colors used
  // For 8+ breaks, we repeat colors to keep Red at the end
  const baseColors = [
    [92, 0, 184, 0.2],       // TCG Purple Dark - 20% opacity (LOWEST)
    [0, 51, 128, 0.15],      // TCG Blue Dark - 15% opacity
    [0, 155, 155, 0.35],     // TCG Cyan Dark - 35% opacity
    [0, 191, 44, 0.35],      // TCG Green Dark - 35% opacity
    [248, 242, 0, 0.35],     // TCG Yellow Dark - 35% opacity
    [255, 122, 13, 0.25],    // TCG Orange Dark - 25% opacity
    [191, 0, 0, 0.4],        // TCG Red Dark - 40% opacity (HIGHEST)
  ];

  // For 7 or fewer breaks, just slice
  if (breakCount <= 7) {
    return baseColors.slice(0, Math.min(breakCount, baseColors.length));
  }

  // For 8-10 breaks, use explicit mappings to spread duplicate colors evenly
  // Duplicates are placed in the middle of the palette for better visual distinction
  const breakColorMappings = {
    8: [0, 1, 2, 3, 3, 4, 5, 6], // Green doubled (middle)
    9: [0, 1, 2, 2, 3, 4, 4, 5, 6], // Cyan and Yellow doubled
    10: [0, 1, 1, 2, 3, 3, 4, 5, 5, 6] // Blue, Green, Orange doubled
  };

  if (breakColorMappings[breakCount]) {
    return breakColorMappings[breakCount].map(idx => baseColors[idx]);
  }

  // Fallback for > 10 breaks: use formula-based distribution
  const result = [];
  for (let i = 0; i < breakCount; i++) {
    if (i === breakCount - 1) {
      // Last break is always Red
      result.push(baseColors[6]);
    } else {
      // Distribute other colors across remaining slots
      const colorIndex = Math.floor((i / (breakCount - 1)) * 6);
      result.push(baseColors[Math.min(colorIndex, 5)]);
    }
  }
  return result;
};

/**
 * Get display title for comp map legend based on field/column name
 * @param {string} fieldName - The field/column name
 * @returns {string} - Display title for legend
 */
const getCompMapLegendTitle = (fieldName) => {
  const fieldTitleMap = {
    // Text fields
    'Comp Name': 'Comp Name',
    'CompName': 'Comp Name',
    'Submarket': 'Submarket',
    'Address': 'Address',
    'City': 'City',
    'Zip': 'Zip Code',
    'Latitude': 'Latitude',
    'Longitude': 'Longitude',
    'Management': 'Management',
    'Product Type': 'Product Type',
    'ProductType': 'Product Type',
    'Map Label': 'Map Label',
    'MapLabel': 'Map Label',
    // Numeric fields with descriptive titles
    'Total Units': 'Building Units',
    'TotalUnits': 'Building Units',
    'Occupancy': 'Occupancy',
    'Year Built': 'Year Built',
    'YearBuilt': 'Year Built',
    'Year Reno.': 'Year Renovated',
    'YearReno': 'Year Renovated',
    'Avg. Size': 'Average Unit Size (SF)',
    'AvgSize': 'Average Unit Size (SF)',
    'Avg. Base Rent': 'Average Base Rent',
    'AvgBaseRent': 'Average Base Rent',
    'Avg. Base PSF': 'Average Base Rent PSF',
    'AvgBasePSF': 'Average Base Rent PSF',
    'Avg. Conc.': 'Average Concession',
    'AvgConc': 'Average Concession',
    'Avg. Net Rent': 'Average Net Rent',
    'AvgNetRent': 'Average Net Rent',
    'Avg. Net PSF': 'Average Net Rent PSF',
    'AvgNetPSF': 'Average Net Rent PSF',
    // Percentage fields
    '% Studio': 'Percent Studio Units',
    '%Studio': 'Percent Studio Units',
    '% Open 1-Bed': 'Percent Open 1-Bed Units',
    '%Open1Bed': 'Percent Open 1-Bed Units',
    '% 1-Bed': 'Percent 1-Bed Units',
    '%1Bed': 'Percent 1-Bed Units',
    '% 2-Bed': 'Percent 2-Bed Units',
    '%2Bed': 'Percent 2-Bed Units',
    '% 3-Bed': 'Percent 3-Bed Units',
    '%3Bed': 'Percent 3-Bed Units',
    '% 4-Bed': 'Percent 4-Bed Units',
    '%4Bed': 'Percent 4-Bed Units',
    '% Other': 'Percent Other Units',
    '%Other': 'Percent Other Units',
    // Other numeric fields
    'Elevation': 'Elevation (Stories)',
    'Walk Score': 'Walk Score',
    'WalkScore': 'Walk Score'
  };

  return fieldTitleMap[fieldName] || fieldName;
};

/**
 * Get formatting function for comp map fields
 * @param {string} fieldName - The field/column name
 * @returns {function} - Formatting function
 */
const getCompMapValueFormatter = (fieldName) => {
  // Formatters for comp map legend values with appropriate prefixes/suffixes
  const formatters = {
    // Text fields - no formatting, return as-is
    'Comp Name': (val) => String(val),
    'CompName': (val) => String(val),
    'Submarket': (val) => String(val),
    'Address': (val) => String(val),
    'City': (val) => String(val),
    'Zip': (val) => String(val), // Text only, no numbers formatting
    'Management': (val) => String(val), // Text Value
    'Product Type': (val) => String(val), // Text Value
    'ProductType': (val) => String(val),
    'Map Label': (val) => String(val), // Text Value
    'MapLabel': (val) => String(val),

    // Coordinate fields - decimal with precision
    'Latitude': (val) => typeof val === 'number' ? val.toFixed(6) : val,
    'Longitude': (val) => typeof val === 'number' ? val.toFixed(6) : val,

    // Total Units: Number with commas, 0 decimals
    'Total Units': (val) => typeof val === 'number' ? Math.round(val).toLocaleString('en-US') : val,
    'TotalUnits': (val) => typeof val === 'number' ? Math.round(val).toLocaleString('en-US') : val,

    // Average Unit Size: Number with commas, 0 decimals
    'Avg. Size': (val) => typeof val === 'number' ? Math.round(val).toLocaleString('en-US') : val,
    'AvgSize': (val) => typeof val === 'number' ? Math.round(val).toLocaleString('en-US') : val,

    // Walk Score: Number with commas, 0 decimals
    'Walk Score': (val) => typeof val === 'number' ? Math.round(val).toLocaleString('en-US') : val,
    'WalkScore': (val) => typeof val === 'number' ? Math.round(val).toLocaleString('en-US') : val,

    // Occupancy: Number with % suffix
    'Occupancy': (val) => typeof val === 'number' ? `${Math.round(val).toLocaleString('en-US')}%` : val,

    // Percentage fields: Convert decimal to percentage (0.25 -> 25%) with % suffix
    '% Studio': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%Studio': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '% Open 1-Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%Open1Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '% 1-Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%1Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '% 2-Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%2Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '% 3-Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%3Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '% 4-Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%4Bed': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '% Other': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,
    '%Other': (val) => typeof val === 'number' ? `${Math.round(val * 100)}%` : val,

    // Year Built: Plain number (no commas for years)
    'Year Built': (val) => typeof val === 'number' ? Math.round(val).toString() : val,
    'YearBuilt': (val) => typeof val === 'number' ? Math.round(val).toString() : val,

    // Year Renovated: Plain number (no commas for years)
    'Year Reno.': (val) => typeof val === 'number' ? Math.round(val).toString() : val,
    'YearReno': (val) => typeof val === 'number' ? Math.round(val).toString() : val,

    // Average Base Rent: $ prefix, number with commas, 0 decimals
    'Avg. Base Rent': (val) => typeof val === 'number' ? `$${Math.round(val).toLocaleString('en-US')}` : val,
    'AvgBaseRent': (val) => typeof val === 'number' ? `$${Math.round(val).toLocaleString('en-US')}` : val,

    // Average Concession: $ prefix, number with commas, 0 decimals
    'Avg. Conc.': (val) => typeof val === 'number' ? `$${Math.round(val).toLocaleString('en-US')}` : val,
    'AvgConc': (val) => typeof val === 'number' ? `$${Math.round(val).toLocaleString('en-US')}` : val,

    // Average Net Rent: $ prefix, number with commas, 0 decimals
    'Avg. Net Rent': (val) => typeof val === 'number' ? `$${Math.round(val).toLocaleString('en-US')}` : val,
    'AvgNetRent': (val) => typeof val === 'number' ? `$${Math.round(val).toLocaleString('en-US')}` : val,

    // Average Base Rent PSF: $ prefix, number with commas, 2 decimals
    'Avg. Base PSF': (val) => typeof val === 'number' ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : val,
    'AvgBasePSF': (val) => typeof val === 'number' ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : val,

    // Average Net Rent PSF: $ prefix, number with commas, 2 decimals
    'Avg. Net PSF': (val) => typeof val === 'number' ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : val,
    'AvgNetPSF': (val) => typeof val === 'number' ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : val,

    // Elevation (Stories): Number with "s" suffix (0 decimals)
    'Elevation': (val) => typeof val === 'number' ? `${Math.round(val)}s` : val
  };

  return formatters[fieldName] || ((val) => typeof val === 'number' ? val.toFixed(2) : val);
};

/**
 * Determine optimal number of breaks for comp maps based on unique value count
 * Updated thresholds based on specification:
 * Breaks:  1   2   3   4    5    6    7    8    9    10
 * Values:  0   2   6   10   17   26   31   43   57   82+
 *
 * @param {number} uniqueValueCount - Number of unique values in the data
 * @returns {number} - Optimal number of breaks (1-10)
 */
const determineCompMapBreakCount = (uniqueValueCount) => {
  if (uniqueValueCount <= 1) return 1;
  if (uniqueValueCount <= 5) return 2;   // 2-5 unique values = 2 breaks
  if (uniqueValueCount <= 9) return 3;   // 6-9 unique values = 3 breaks
  if (uniqueValueCount <= 16) return 4;  // 10-16 unique values = 4 breaks
  if (uniqueValueCount <= 25) return 5;  // 17-25 unique values = 5 breaks
  if (uniqueValueCount <= 30) return 6;  // 26-30 unique values = 6 breaks
  if (uniqueValueCount <= 42) return 7;  // 31-42 unique values = 7 breaks
  if (uniqueValueCount <= 56) return 8;  // 43-56 unique values = 8 breaks
  if (uniqueValueCount <= 81) return 9;  // 57-81 unique values = 9 breaks
  return 10; // 82+ unique values = 10 breaks
};

/**
 * Generates unique value breaks for text/categorical fields in comp maps
 * Used for fields like Zip, Management, Product Type, Map Label
 * Each unique value gets its own color - no "Less than" or "more than" labels
 *
 * @param {Array} data - Array of data objects
 * @param {string} valueColumn - Column name for text values
 * @returns {Array} - Array of unique value break objects
 */
const generateCompMapTextBreaks = (data, valueColumn) => {
  // TCG Color palette for comp maps (10 colors) - ordered from highest to lowest value
  // Red Dark = highest value, Carbon Gray Light = lowest value
  const TCG_COLORS_ARRAY = [
    [191, 0, 0, 0.8],       // 1. TCG Red Dark (HIGHEST)
    [255, 122, 13, 0.8],    // 2. TCG Orange Dark
    [248, 242, 0, 0.8],     // 3. Yellow Dark
    [0, 191, 44, 0.8],      // 4. TCG Green Dark
    [0, 155, 155, 0.8],     // 5. TCG Cyan Dark
    [0, 51, 128, 0.8],      // 6. TCG Blue Dark
    [92, 0, 184, 0.8],      // 7. TCG Purple Dark
    [214, 0, 158, 0.8],     // 8. Pink Dark
    [148, 112, 60, 0.8],    // 9. Brown Dark
    [174, 170, 170, 0.8]    // 10. Carbon Gray Light (LOWEST)
  ];

  // Extract unique text values
  const uniqueValues = [...new Set(
    data
      .map(item => item[valueColumn])
      .filter(val => val !== null && val !== undefined && val !== '')
      .map(val => String(val))
  )].sort();

  if (uniqueValues.length === 0) {
    console.warn(`[generateCompMapTextBreaks] No valid text data in '${valueColumn}'`);
    return [];
  }

  console.log(`[generateCompMapTextBreaks] Found ${uniqueValues.length} unique values for "${valueColumn}"`);

  // Determine optimal break count based on unique value count
  const breakCount = Math.min(uniqueValues.length, 10);

  // Get the colors to use (first N colors from palette)
  const colorsToUse = TCG_COLORS_ARRAY.slice(0, breakCount);

  // Create a break for each unique value - labels are just the text value (no "Less than" or "more than")
  // For text fields, assign colors sequentially (first value gets Red, then Orange, etc.)
  // This matches the pattern: Red is always break 1, colors added sequentially
  const classBreaks = uniqueValues.map((value, index) => {
    // Direct color assignment: first unique value gets Red (index 0), second gets Orange, etc.
    const colorIndex = index % colorsToUse.length;

    return {
      value: value, // Store the actual value for unique value rendering
      minValue: index, // Use index for ordering
      maxValue: index,
      isTextField: true, // Flag to indicate this is a text field break
      symbol: {
        type: "simple-marker",
        size: 28,
        color: colorsToUse[colorIndex],
        outline: {
          color: [0, 0, 0, 0.8],
          width: 2
        }
      },
      label: value // Just the text value, no "Less than" or "more than"
    };
  });

  console.log(`[generateCompMapTextBreaks] Generated ${classBreaks.length} text breaks for "${valueColumn}"`);
  return classBreaks;
};

/**
 * Generates class breaks for comp map data visualization
 * Creates evenly distributed breaks using TCG color palette
 * Number of breaks is determined by data point count
 *
 * @param {Array} data - Array of data objects
 * @param {string} valueColumn - Column name for values
 * @param {number} numClasses - Number of classes to create (optional, auto-calculated if not provided)
 * @returns {Array} - Array of class break objects
 */
/**
 * Checks if a field should be treated as a text/categorical field for comp maps
 * Text fields use unique value rendering instead of class breaks
 */
const isCompTextField = (fieldName) => {
  const textFields = [
    'Zip', 'Management', 'Product Type', 'ProductType', 'Map Label', 'MapLabel',
    'Comp Name', 'CompName', 'Submarket', 'Address', 'City'
  ];
  return textFields.includes(fieldName);
};

/**
 * Checks if a field is a year field (Year Built, Year Reno.)
 * Year fields use "or earlier"/"or later" instead of "Less than"/"or more"
 */
const isYearField = (fieldName) => {
  const yearFields = ['Year Built', 'YearBuilt', 'Year Reno.', 'YearReno'];
  return yearFields.includes(fieldName);
};

const generateCompMapClassBreaks = (data, valueColumn, numClasses = null) => {
  if (!data || data.length === 0 || !valueColumn) {
    console.warn('[generateCompMapClassBreaks] Invalid data or value column');
    return [];
  }

  // Check if this is a text/categorical field - use unique values instead of class breaks
  if (isCompTextField(valueColumn)) {
    console.log(`[generateCompMapClassBreaks] "${valueColumn}" is a text field, using unique value breaks`);
    return generateCompMapTextBreaks(data, valueColumn);
  }

  // Extract numeric values
  const values = data.map(item => {
    const rawValue = item[valueColumn];
    if (rawValue === null || rawValue === undefined || rawValue === '') return NaN;
    const num = Number(rawValue);
    return isNaN(num) ? NaN : num;
  }).filter(val => !isNaN(val));

  if (values.length === 0) {
    console.warn(`[generateCompMapClassBreaks] No valid numeric data in '${valueColumn}'`);
    return [];
  }

  // Count unique values for determining optimal break count
  const uniqueValues = [...new Set(values)];
  const uniqueValueCount = uniqueValues.length;

  // Determine optimal number of breaks based on UNIQUE VALUE count (not data point count)
  const optimalBreakCount = numClasses !== null ? numClasses : determineCompMapBreakCount(uniqueValueCount);
  console.log(`[generateCompMapClassBreaks] Data points: ${data.length}, unique values: ${uniqueValueCount}, using ${optimalBreakCount} breaks`);

  // Get the formatter for this field
  const formatter = getCompMapValueFormatter(valueColumn);

  if (values.length === 1 || uniqueValueCount === 1) {
    // Single value case - use Red (highest value color)
    const singleValue = values[0];
    return [{
      minValue: singleValue,
      maxValue: singleValue,
      symbol: {
        type: "simple-marker",
        size: 28,
        color: [191, 0, 0, 0.8], // TCG Red Dark (highest value)
        outline: {
          color: [0, 0, 0, 0.8],
          width: 2
        }
      },
      label: formatter(singleValue)
    }];
  }

  // Calculate min and max
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (minValue === maxValue) {
    // All values are the same - use Red (highest value color)
    return [{
      minValue: minValue,
      maxValue: maxValue,
      symbol: {
        type: "simple-marker",
        size: 28,
        color: [191, 0, 0, 0.8], // TCG Red Dark (highest value)
        outline: {
          color: [0, 0, 0, 0.8],
          width: 2
        }
      },
      label: formatter(minValue)
    }];
  }

  // Create even intervals using the optimal break count
  const range = maxValue - minValue;
  const interval = range / optimalBreakCount;

  // Create break points for equal intervals with no gaps
  const breakPoints = [];
  const labels = [];

  for (let i = 0; i < optimalBreakCount; i++) {
    let min, max;

    if (i === 0) {
      // First break: -Infinity to first threshold
      min = -Infinity;
      max = minValue + interval;
    } else if (i === optimalBreakCount - 1) {
      // Last break: previous max to Infinity
      min = breakPoints[i - 1].max;
      max = Infinity;
    } else {
      // Middle breaks: previous max to next threshold
      min = breakPoints[i - 1].max;
      max = minValue + ((i + 1) * interval);
    }

    const roundedMin = min === -Infinity ? min : Math.round(min * 100) / 100;
    const roundedMax = max === Infinity ? max : Math.round(max * 100) / 100;

    breakPoints.push({
      min: roundedMin,
      max: roundedMax
    });

    // Generate descriptive labels for each break with proper formatting
    // Year fields use "or earlier"/"or later" instead of "Less than"/"or more"
    const isYear = isYearField(valueColumn);
    if (i === 0) {
      const formattedMax = roundedMax === Infinity ? 'max' : formatter(roundedMax);
      labels.push(isYear ? `${formattedMax} or earlier` : `Less than ${formattedMax}`);
    } else if (i === optimalBreakCount - 1) {
      const formattedMin = roundedMin === -Infinity ? 'min' : formatter(roundedMin);
      labels.push(isYear ? `${formattedMin} or later` : `${formattedMin} or more`);
    } else {
      const formattedMin = roundedMin === -Infinity ? 'min' : formatter(roundedMin);
      const formattedMax = roundedMax === Infinity ? 'max' : formatter(roundedMax);
      labels.push(`${formattedMin} - ${formattedMax}`);
    }
  }

  // TCG Color palette for comp maps (10 colors) - ordered from HIGHEST to LOWEST value
  // Red Dark = highest value, Carbon Gray Light = lowest value
  const TCG_COLORS_ARRAY = [
    [191, 0, 0, 0.8],       // 1. TCG Red Dark (HIGHEST)
    [255, 122, 13, 0.8],    // 2. TCG Orange Dark
    [248, 242, 0, 0.8],     // 3. Yellow Dark
    [0, 191, 44, 0.8],      // 4. TCG Green Dark
    [0, 155, 155, 0.8],     // 5. TCG Cyan Dark
    [0, 51, 128, 0.8],      // 6. TCG Blue Dark
    [92, 0, 184, 0.8],      // 7. TCG Purple Dark
    [214, 0, 158, 0.8],     // 8. Pink Dark
    [148, 112, 60, 0.8],    // 9. Brown Dark
    [174, 170, 170, 0.8]    // 10. Carbon Gray Light (LOWEST)
  ];

  // Use only the first optimalBreakCount colors from the TCG palette
  const colorsToUse = TCG_COLORS_ARRAY.slice(0, optimalBreakCount);

  // Create class breaks with TCG colors for comp maps
  // REVERSED color assignment: Red is always break 1 (highest values)
  // For 3 breaks: highest→Red, middle→Orange, lowest→Yellow
  // For 5 breaks: highest→Red, ...middle..., lowest→Cyan
  const classBreaks = breakPoints.map((point, index) => {
    // Reverse the color index: highest break (last index) gets Red (index 0)
    const reversedColorIndex = optimalBreakCount - 1 - index;

    return {
      minValue: point.min === undefined ? -Infinity : point.min,
      maxValue: point.max === undefined ? Infinity : point.max,
      symbol: {
        type: "simple-marker",
        size: 28,
        color: colorsToUse[reversedColorIndex],
        outline: {
          color: [0, 0, 0, 0.8],
          width: 2
        }
      },
      label: labels[index]
    };
  });

  console.log(`[generateCompMapClassBreaks] Generated ${classBreaks.length} class breaks with TCG colors for range ${minValue}-${maxValue}`);
  return classBreaks;
};

/**
 * Utility function to create default dot density colors
 * 
 * @param {number} attributeCount - Number of attributes to create colors for
 * @returns {Array} - Array of RGB color arrays
 */
const createDefaultDotDensityColors = (attributeCount) => {
  const dotColors = [
    [227, 26, 28],    // Red
    [55, 126, 184],   // Blue
    [77, 175, 74],    // Green
    [152, 78, 163],   // Purple
    [255, 127, 0],    // Orange
    [255, 255, 51],   // Yellow
    [166, 86, 40],    // Brown
    [247, 129, 191],  // Pink
    [153, 153, 153]   // Gray
  ];
  
  return dotColors.slice(0, Math.min(attributeCount, dotColors.length));
};

/**
 * Formats a point label with optional variable inclusions
 * 
 * @param {Object} item - The data item containing label and variable values
 * @param {Object} options - Formatting options
 * @param {string} options.labelColumn - Column name for the main label
 * @param {string} options.variable1Column - Column name for first variable
 * @param {string} options.variable2Column - Column name for second variable
 * @param {Object} options.labelOptions - Additional label formatting options
 * @param {boolean} options.labelOptions.includeVariables - Whether to include variables in label
 * @returns {string} Formatted label text
 */
export function formatPointLabel(item, options) {
  // Extract options with defaults
  const {
    labelColumn,
    variable1Column,
    variable2Column,
    labelOptions = {}
  } = options || {};
  
  // Default to true if not explicitly set to false
  const includeVariables = labelOptions.includeVariables !== false;
  
  // Format the main label
  let labelText = '';
  
  // Use labelColumn if available
  if (labelColumn && item[labelColumn] !== undefined && item[labelColumn] !== null) {
    labelText = String(item[labelColumn]);
  } else {
    // Fallback to object ID or index if available
    labelText = item.OBJECTID !== undefined ? `Point ${item.OBJECTID}` : 
               (item.id !== undefined ? `Point ${item.id}` : 'Unnamed Point');
  }
  
  // Include variables if configured and available
  if (includeVariables) {
    const variables = [];
    
    // Add variable 1 if available
    if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) {
      variables.push(String(item[variable1Column]));
    }
    
    // Add variable 2 if available
    if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) {
      variables.push(String(item[variable2Column]));
    }
    
    // Append variables in parentheses if we have any
    if (variables.length > 0) {
      labelText += ` (${variables.join(', ')})`;
    }
  }
  
  return labelText;
}

/**
 * Generates a title for point popups
 * 
 * @param {Object} item - The data item containing label and variable values
 * @param {Object} options - Formatting options
 * @param {string} options.labelColumn - Column name for the main label
 * @param {string} options.variable1Column - Column name for first variable
 * @param {string} options.variable2Column - Column name for second variable
 * @returns {string} Formatted popup title
 */
export function generatePointTitle(item, options) {
  // Extract options with defaults
  const {
    labelColumn,
    variable1Column,
    variable2Column
  } = options || {};
  
  // Format the main title
  let title = '';
  
  // Use labelColumn if available
  if (labelColumn && item[labelColumn] !== undefined && item[labelColumn] !== null) {
    title = String(item[labelColumn]);
  } else {
    // Fallback to object ID or index if available
    title = item.OBJECTID !== undefined ? `Point ${item.OBJECTID}` : 
           (item.id !== undefined ? `Point ${item.id}` : 'Unnamed Point');
  }
  
  // Always include variables in the title for better identification
  const variables = [];
  
  // Add variable 1 if available
  if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) {
    variables.push(String(item[variable1Column]));
  }
  
  // Add variable 2 if available
  if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) {
    variables.push(String(item[variable2Column]));
  }
  
  // Append variables in parentheses if we have any
  if (variables.length > 0) {
    title += ` (${variables.join(', ')})`;
  }
  
  return title;
}

/**
 * Creates a comparison map layer with improved label handling
 * 
 * @param {Object} config - Configuration for the comp layer
 * @returns {GraphicsLayer} The configured comparison graphics layer
 */
export const createCompLayer = (config) => {
  console.log("[createCompLayer] Received config:", JSON.stringify(config, (k,v) => k === 'data' ? `[${v?.length} items]` : v));
  
  // Extract mapConfigId from config or from session storage as fallback
  let determinedMapConfigId = config.configId || config.mapConfigId;
  if (!determinedMapConfigId && typeof sessionStorage !== 'undefined') {
      determinedMapConfigId = sessionStorage.getItem("currentMapConfigId");
  }
  const mapConfigId = determinedMapConfigId; // Use the correctly determined ID

  // Add more detailed logging to confirm:
  console.log(`[createCompLayer/PipeLayer] Using mapConfigId: ${mapConfigId} for layer title: ${config?.title}. \
  Derived from config.configId: ${config.configId}, \
  config.mapConfigId: ${config.mapConfigId}, \
  sessionStorage('currentMapConfigId'): ${typeof sessionStorage !== 'undefined' ? sessionStorage.getItem("currentMapConfigId") : 'N/A'}`);  console.log(`[createCompLayer] Using mapConfigId: ${mapConfigId} for layer title: ${config?.title}`);
  
  // Create the graphics layer
  const graphicsLayer = new GraphicsLayer({
    title: config?.title || "Comparison Map Layer",
    listMode: "show"
  });
  
  // IMPORTANT: Explicitly set the mapConfigId on the layer
  graphicsLayer.mapConfigId = mapConfigId;
  console.log(`[createCompLayer] Set mapConfigId on layer: ${mapConfigId}`);
  
  // Flag to indicate this layer has its own label graphics
  graphicsLayer.hasLabelGraphics = true;

  // --- Extract data and column information from user's provided full function ---
  const data = config?.customData?.data || [];
  const labelColumn = config?.labelColumn;
  const variable1Column = config?.variable1Column;
  const variable2Column = config?.variable2Column;
  const variable1Text = config?.variable1Text || '';
  const variable2Text = config?.variable2Text || '';
  const variable1Prefix = config?.variable1Prefix || '';
  const variable2Prefix = config?.variable2Prefix || '';
  const valueColumn = config?.valueColumn || config?.field;
  const latitudeColumn = config?.latitudeColumn;
  const longitudeColumn = config?.longitudeColumn;
  const symbol = config?.symbol || {};
  let classBreakInfos = config?.classBreakInfos || [];
  let rendererType = config?.rendererType || (classBreakInfos.length > 0 ? 'classBreaks' : 'simple');

  // Auto-detect value format based on the valueColumn if not explicitly provided
  let valueFormat = config?.valueFormat;
  if (!valueFormat && valueColumn) {
    valueFormat = getCompFieldFormat(valueColumn);
    console.log(`[createCompLayer] Auto-detected format for "${valueColumn}":`, valueFormat);
  }

  // Generate class breaks if value column is specified but no class breaks are provided
  if (valueColumn && data.length > 0 && classBreakInfos.length === 0) {
    console.log('[createCompLayer] Generating class breaks for value column:', valueColumn);

    // Let generateCompMapClassBreaks auto-calculate the optimal number based on data count
    const generatedBreaks = generateCompMapClassBreaks(data, valueColumn);
    if (generatedBreaks && generatedBreaks.length > 0) {
      classBreakInfos = generatedBreaks;
      rendererType = 'classBreaks';
      console.log(`[createCompLayer] Generated ${classBreakInfos.length} class breaks for comp map`);
    } else {
      console.warn('[createCompLayer] Failed to generate class breaks, using simple renderer');
    }
  }
  
  const labelOptions = config?.labelOptions || {};
  const fontSize = labelOptions.fontSize || 10;
  const fontFamily = labelOptions.fontFamily || 'Arial';
  const includeVariables = labelOptions.includeVariables !== false;
  const visibleAtAllZooms = labelOptions.visibleAtAllZooms || false;
  const minZoom = labelOptions.minZoom || 10;

  // Check if labels should be disabled
  const disableLabels = config?.hasNoLabels || config?.hideAllLabels || false;

  graphicsLayer.labelVisibilityTracking = {
    enabled: !visibleAtAllZooms,
    minimumZoom: minZoom,
    currentlyVisible: false
  };

  if (!Array.isArray(data) || data.length === 0 || !latitudeColumn || !longitudeColumn) {
    console.warn("[createCompLayer] Missing required data for comp layer: customData array, latitudeColumn, or longitudeColumn. Layer will be empty.");
    // Ensure mapConfigId is included in the labelFormatInfo even for an empty layer context
    graphicsLayer.labelFormatInfo = {
        labelColumn, variable1Column, variable2Column, variable1Text, variable2Text, variable1Prefix, variable2Prefix, includeVariables,
        mapConfigId: mapConfigId // Explicitly include mapConfigId
    };
    return graphicsLayer;
  }

  let addedPoints = 0;
  let addedLabels = 0;
  
  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    if (!item[latitudeColumn] || !item[longitudeColumn]) continue;
    const lat = parseFloat(item[latitudeColumn]);
    const lon = parseFloat(item[longitudeColumn]);
    if (isNaN(lat) || isNaN(lon)) continue;

    const pointValue = valueColumn ? item[valueColumn] : null;
    let pointColor = symbol.color || "#800080";

    if (pointValue !== undefined && pointValue !== null &&
        rendererType === 'classBreaks' && classBreakInfos && classBreakInfos.length > 0) {

      // Check if this is categorical data (first break has isCategorical flag or value property)
      const isCategorical = classBreakInfos[0]?.isCategorical || classBreakInfos[0]?.value !== undefined;

      if (isCategorical) {
        // Match by categorical value (string comparison)
        const stringValue = String(pointValue).trim();
        for (const breakInfo of classBreakInfos) {
          if (breakInfo.value === stringValue || breakInfo.label === stringValue) {
            pointColor = breakInfo.symbol?.color || pointColor;
            break;
          }
        }
      } else {
        // Match by numeric value ranges (original logic)
        let cleanedValue = pointValue;
        if (typeof pointValue === 'string') {
          cleanedValue = pointValue.trim().replace(/s$/i, '').replace(/ft$/i, '').replace(/stories?$/i, '');
        }
        const numValue = Number(cleanedValue);
        if (!isNaN(numValue)) {
          for (const breakInfo of classBreakInfos) {
            const maxVal = (breakInfo.maxValue === Infinity || breakInfo.maxValue === undefined) ? Infinity : Number(breakInfo.maxValue);
            const minVal = (breakInfo.minValue === -Infinity || breakInfo.minValue === undefined) ? -Infinity : Number(breakInfo.minValue);
            if ((minVal === -Infinity || numValue >= minVal) && (maxVal === Infinity || numValue <= maxVal)) {
              pointColor = breakInfo.symbol?.color || pointColor;
              break;
            }
          }
        }
      }
    }

    try {
      const point = new Point({ x: lon, y: lat, spatialReference: { wkid: 4326 } });
      const pointSize = symbol.size !== undefined ? Number(symbol.size) : 12;
      const pointSymbol = new SimpleMarkerSymbol({
        style: symbol.style || "circle",
        size: pointSize,
        color: new Color(pointColor),
        outline: {
          color: new Color(symbol.outline?.color || "#FFFFFF"),
          width: symbol.outline?.width !== undefined ? Number(symbol.outline.width) : 0
        }
      });

      let pointTitle = item[labelColumn] || `Point ${i}`;
      const variableParts = [];
      if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) {
        variableParts.push((variable1Prefix || '') + item[variable1Column] + (variable1Text || ''));
      }
      if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) {
        variableParts.push((variable2Prefix || '') + item[variable2Column] + (variable2Text || ''));
      }
      if (variableParts.length > 0 && includeVariables) {
        pointTitle += ' (' + variableParts.join(', ') + ')';
      }

      const graphic = new Graphic({
        geometry: point,
        symbol: pointSymbol,
        attributes: {
          ...item, OBJECTID: i, displayValue: pointValue, displayName: pointTitle,
          variable1Text: variable1Text, variable2Text: variable2Text, variable1Prefix: variable1Prefix, variable2Prefix: variable2Prefix, hasCustomFormat: true
        },
        popupTemplate: new PopupTemplate({
          title: "{displayName}",
          content: valueColumn ? [{
            type: "fields",
            fieldInfos: [{
              fieldName: "displayValue", label: valueColumn || "Value",
              format: valueFormat ? { digitSeparator: true, places: valueFormat.decimals ?? 0, prefix: valueFormat.prefix || '', suffix: valueFormat.suffix || '' } : { digitSeparator: true, places: 2 }
            }]
          }] : [{ type: "text", text: "No value column selected for display." }]
        })
      });
      graphicsLayer.add(graphic);
      addedPoints++;

      // Only create label graphics if labels are not disabled
      if (!disableLabels) {
        let labelText = item[labelColumn] || `Point ${i}`;
        if (includeVariables) {
          const labelVariableParts = [];
          if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) {
            labelVariableParts.push((variable1Prefix || '') + item[variable1Column] + (variable1Text || ''));
          }
          if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) {
            labelVariableParts.push((variable2Prefix || '') + item[variable2Column] + (variable2Text || ''));
          }
          if (labelVariableParts.length > 0) {
            labelText += ' (' + labelVariableParts.join(', ') + ')';
          }
        }

        // Create offset point for label positioning (geographic coordinates)
        // Vary the initial position based on the index to reduce initial collisions
        // Prioritize left/right placement before moving labels farther away
        // Use appropriate horizontal alignment so labels don't overlap with their points
        const positionPatterns = [
          { latOffset: 0, lonOffset: -0.0001, hAlign: "right" },   // Left (text extends left from anchor)
          { latOffset: 0, lonOffset: 0.0001, hAlign: "left" },     // Right (text extends right from anchor)
          { latOffset: 0.0006, lonOffset: 0, hAlign: "center" },   // Above
          { latOffset: -0.0006, lonOffset: 0, hAlign: "center" },  // Below
          { latOffset: 0.0004, lonOffset: -0.0001, hAlign: "right" }, // Upper left
          { latOffset: -0.0004, lonOffset: -0.0001, hAlign: "right" }, // Lower left
          { latOffset: 0.0004, lonOffset: 0.0001, hAlign: "left" },  // Upper right
          { latOffset: -0.0004, lonOffset: 0.0001, hAlign: "left" } // Lower right
        ];

        const pattern = positionPatterns[i % positionPatterns.length];
        const offsetLatLng = {
          latitude: lat + pattern.latOffset,
          longitude: lon + pattern.lonOffset
        };

        const labelPoint = new Point({
          x: offsetLatLng.longitude,  // ArcGIS uses x for longitude
          y: offsetLatLng.latitude,   // ArcGIS uses y for latitude
          spatialReference: { wkid: 4326 }
        });

        const whiteBackground = labelOptions.whiteBackground || false;
        const showMask = !whiteBackground && (labelOptions.mask !== false); // mask defaults to true
        const textSymbol = new TextSymbol({
          text: labelText,
          font: { size: fontSize, family: fontFamily, weight: labelOptions.bold ? "bold" : "normal" },
          color: new Color([0, 0, 0, 0.9]),
          haloColor: showMask ? new Color([255, 255, 255, 0.9]) : null,
          haloSize: showMask ? 1 : 0,
          backgroundColor: whiteBackground ? [255, 255, 255, 0.9] : null,
          borderLineColor: whiteBackground ? [255, 255, 255, 0.9] : null,
          borderLineSize: whiteBackground ? 4 : 0,
          // NO pixel offsets - using geographic positioning
          xoffset: 0,
          yoffset: 0,
          horizontalAlignment: pattern.hAlign,  // Dynamic alignment based on position
          verticalAlignment: "middle"
        });
        const labelGraphic = new Graphic({
          geometry: labelPoint, symbol: textSymbol,
          attributes: {
            ...item, OBJECTID: `label-${i}`, parentID: i, isLabel: true, visible: true,
            variable1Column: variable1Column, variable2Column: variable2Column,
            variable1Text: variable1Text, variable2Text: variable2Text,
            hasCustomFormat: true, labelText: labelText, baseText: item[labelColumn] || `Point ${i}`,
            dataPointGeometry: point.clone(),  // Store original data point location for label collision detection
            markerSize: pointSize  // Store marker size for collision boundary scaling
          },
          visible: visibleAtAllZooms
        });
        graphicsLayer.add(labelGraphic);
        addedLabels++;
      }
    } catch (err) {
      console.error(`[createCompLayer] Error creating graphics for item ${i}:`, err);
    }
  }

  console.log(`[createCompLayer] Added ${addedPoints} points and ${addedLabels} labels to comp layer "${graphicsLayer.title}" with config ${mapConfigId}.`);
  
  graphicsLayer.variable1Text = variable1Text;
  graphicsLayer.variable2Text = variable2Text;
  graphicsLayer.variable1Prefix = variable1Prefix;
  graphicsLayer.variable2Prefix = variable2Prefix;
  // graphicsLayer.mapConfigId is already set at the beginning

  // Ensure mapConfigId and valueFormat are included in the labelFormatInfo
  graphicsLayer.labelFormatInfo = {
    labelColumn,
    variable1Column,
    variable2Column,
    variable1Text,
    variable2Text,
    variable1Prefix,
    variable2Prefix,
    includeVariables,
    valueFormat, // Include valueFormat for legend rendering
    valueColumn, // Include valueColumn to identify the field being displayed
    mapConfigId: mapConfigId // Explicitly include mapConfigId
  };
  
  return graphicsLayer;
};

/**
 * Creates a pipeline map layer with enhanced label handling
 * 
 * @param {Object} config - Configuration for the pipe layer
 * @returns {GraphicsLayer} The configured pipeline graphics layer
 */
export const createPipeLayer = (config) => {
  console.log("[createPipeLayer] Creating Pipe Layer with config:", JSON.stringify(config, (k,v) => k === 'data' ? `[${v?.length} items]` : v));
  
    // Extract mapConfigId from config or from session storage as fallback
  let determinedMapConfigId = config.configId || config.mapConfigId;
  if (!determinedMapConfigId && typeof sessionStorage !== 'undefined') {
      determinedMapConfigId = sessionStorage.getItem("currentMapConfigId");
  }
  const mapConfigId = determinedMapConfigId; // Use the correctly determined ID

  // Add more detailed logging to confirm:
  console.log(`[createCompLayer/PipeLayer] Using mapConfigId: ${mapConfigId} for layer title: ${config?.title}. \
  Derived from config.configId: ${config.configId}, \
  config.mapConfigId: ${config.mapConfigId}, \
  sessionStorage('currentMapConfigId'): ${typeof sessionStorage !== 'undefined' ? sessionStorage.getItem("currentMapConfigId") : 'N/A'}`);  console.log(`[createPipeLayer] Using mapConfigId: ${mapConfigId} for layer title: ${config?.title}`);
  
  // Early validation check - quit if critical data is missing
  if (!config || !config.customData || !Array.isArray(config.customData.data) || 
      config.customData.data.length === 0 || !config.latitudeColumn || !config.longitudeColumn) {
    console.warn("[createPipeLayer] Missing required data for pipeline layer: customData, latitudeColumn, or longitudeColumn. Returning empty layer.");
    // Return a valid but empty layer instead of null to avoid errors
    const emptyLayer = new GraphicsLayer({
      title: config?.title || "Pipeline Map Layer (Empty)",
      listMode: "show"
    });
    emptyLayer.mapConfigId = mapConfigId; // Set mapConfigId even on empty layer
    // Ensure labelFormatInfo is present with mapConfigId
    emptyLayer.labelFormatInfo = {
        labelColumn: config?.labelColumn,
        variable1Column: config?.variable1Column,
        variable2Column: config?.variable2Column,
        variable1Text: config?.variable1Text || '',
        variable2Text: config?.variable2Text || '',
        variable1Prefix: config?.variable1Prefix || '',
        variable2Prefix: config?.variable2Prefix || '',
        includeVariables: config?.labelOptions?.includeVariables !== false,
        mapConfigId: mapConfigId
    };
    return emptyLayer;
  }
  
  // Create the graphics layer
  const graphicsLayer = new GraphicsLayer({
    title: config?.title || "Pipeline Map Layer",
    listMode: "show"
  });

  // IMPORTANT: Explicitly set the mapConfigId on the layer
  graphicsLayer.mapConfigId = mapConfigId;
  console.log(`[createPipeLayer] Set mapConfigId on layer: ${mapConfigId}`);

  // Add this flag to indicate this layer manages its own labels
  graphicsLayer.hasLabelGraphics = true;
  
  // --- Extract configuration from user's provided full function ---
  const {
    customData, labelColumn, variable1Column, variable2Column, statusColumn,
    valueColumn, latitudeColumn, longitudeColumn, symbol = {}, statusColors = {},
    classBreakInfos = [],
    rendererType = classBreakInfos.length > 0 ? 'classBreaks' : (statusColumn ? 'uniqueValue' : 'simple')
  } = config;

  // Auto-detect status column if not explicitly set
  let detectedStatusColumn = statusColumn;
  if (!detectedStatusColumn && customData && customData.data && customData.data.length > 0) {
    const firstRow = customData.data[0];
    const columns = Object.keys(firstRow);
    const statusKeywords = ['status', 'state', 'condition', 'phase', 'stage', 'progress', 'pipeline', 'type'];
    detectedStatusColumn = columns.find(col =>
      statusKeywords.some(keyword => col.toLowerCase().includes(keyword))
    );
    if (detectedStatusColumn) {
      console.log(`[createPipeLayer] Auto-detected status column: "${detectedStatusColumn}"`);
    }
  }

  // Use detected status column for renderer type determination
  const finalStatusColumn = detectedStatusColumn || statusColumn;
  const finalRendererType = classBreakInfos.length > 0 ? 'classBreaks' : (finalStatusColumn ? 'uniqueValue' : 'simple');

  console.log("[createPipeLayer] Configuration details:", {
    hasStatusColumn: !!finalStatusColumn,
    statusColumn: finalStatusColumn,
    hasStatusColors: !!statusColors && Object.keys(statusColors).length > 0,
    statusColorKeys: Object.keys(statusColors || {}),
    rendererType: finalRendererType,
    configKeys: Object.keys(config),
    autoDetected: !!detectedStatusColumn && !statusColumn,
    sampleDataRow: customData?.data?.[0] ? Object.keys(customData.data[0]) : 'No data'
  });

  // Log sample status values from the data
  if (finalStatusColumn && customData?.data?.length > 0) {
    const statusValues = customData.data.slice(0, 5).map(row => row[finalStatusColumn]).filter(v => v);
    console.log(`[createPipeLayer] Sample status values from "${finalStatusColumn}":`, statusValues);
  }
  
  const variable1Text = config.variable1Text || '';
  const variable2Text = config.variable2Text || '';
  const variable1Prefix = config.variable1Prefix || '';
  const variable2Prefix = config.variable2Prefix || '';

  const labelOptions = config?.labelOptions || {};
  const fontSize = labelOptions.fontSize || 10;
  const fontFamily = labelOptions.fontFamily || 'Arial';
  const includeVariables = labelOptions.includeVariables !== false;
  const avoidCollisions = labelOptions.avoidCollisions !== false;
  const visibleAtAllZooms = labelOptions.visibleAtAllZooms || false;
  const minZoom = labelOptions.minZoom || 10;

  // Check if labels should be disabled
  const disableLabels = config?.hasNoLabels || config?.hideAllLabels || false;

  graphicsLayer.labelVisibilityTracking = { 
    enabled: !visibleAtAllZooms,
    minimumZoom: minZoom,
    currentlyVisible: false
  };
  
  const defaultSymbol = { type: "simple-marker", style: "circle", size: 12, color: "#FFA500", outline: { color: "#FFFFFF", width: 0 } };
  const symbolSize = symbol.size !== undefined ? Number(symbol.size) : defaultSymbol.size;
  const completeSymbol = {
    type: symbol.type || defaultSymbol.type, style: symbol.style || defaultSymbol.style, size: symbolSize,
    color: symbol.color || defaultSymbol.color,
    outline: { color: symbol.outline?.color || defaultSymbol.outline.color, width: symbol.outline?.width !== undefined ? Number(symbol.outline.width) : defaultSymbol.outline.width }
  };
  const defaultStatusColors = {
    "Under Construction": "#BF0000",    // TCG Red Dark
    "Approved": "#FF7A0D",              // Orange Dark
    "Pending": "#F8F200",               // Yellow Dark
    "Proposed": "#00BF2C",              // TCG Green Dark
    "Conceptual": "#009B9B",            // TCG Cyan Dark
    "Stalled": "#003380",               // TCG Blue Dark
    "Inactive": "#5C00B8",              // TCG Purple Dark
    "Pre-Leasing/Pre-Selling": "#D6009E", // Pink Dark
    "Pre-Leasing": "#D6009E",           // Pink Dark
    "Pre-Selling": "#D6009E",           // Pink Dark
    "Complete": "#AEAAAA",              // Carbon Gray Light
    "Completed": "#00BCD4",
    "Active": "#4CAF50",
    "In Progress": "#FF9800",
    "Planning": "#9C27B0",
    "On Hold": "#F44336",
    "Cancelled": "#9E9E9E",
    "default": "#FFA500"
  };
  const mergedStatusColors = { ...defaultStatusColors, ...statusColors };
  
  try {
    const dataArray = customData.data || customData; // customData might be the array itself
    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      console.warn("[createPipeLayer] Pipeline layer has no valid data points after extraction. Layer will be empty.");
      graphicsLayer.labelFormatInfo = { labelColumn, variable1Column, variable2Column, variable1Text, variable2Text, variable1Prefix, variable2Prefix, includeVariables, mapConfigId: mapConfigId };
      return graphicsLayer;
    }
    
    const labelPositions = new Map();
    if (avoidCollisions) {
      const pointData = dataArray.map((item, index) => {
        if (!item[latitudeColumn] || !item[longitudeColumn]) return null;
        const lat = parseFloat(item[latitudeColumn]);
        const lon = parseFloat(item[longitudeColumn]);
        if (isNaN(lat) || isNaN(lon)) return null;
        let labelText = item[labelColumn] || `Point ${index}`;
        if (includeVariables) {
          const labelVariableParts = [];
          if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) labelVariableParts.push((variable1Prefix || '') + item[variable1Column] + (variable1Text || ''));
          if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) labelVariableParts.push((variable2Prefix || '') + item[variable2Column] + (variable2Text || ''));
          if (labelVariableParts.length > 0) labelText += ' (' + labelVariableParts.join(', ') + ')';
        }
        return { id: `label-${index}`, latitude: lat, longitude: lon, labelText, priority: index < 20 ? 2 : 1 };
      }).filter(Boolean);
      
      const positions = pointData.map((point, index) => {
        const offset = [0, fontSize + 4];
        const patternIndex = index % 8;
        const directions = [[0, fontSize + 4], [fontSize + 4, 0], [0, -(fontSize + 4)], [-(fontSize + 4), 0], [fontSize + 4, fontSize + 4], [-(fontSize + 4), fontSize + 4], [fontSize + 4, -(fontSize + 4)], [-(fontSize + 4), -(fontSize + 4)]];
        offset[0] = directions[patternIndex][0]; offset[1] = directions[patternIndex][1];
        return { id: point.id, x: offset[0], y: offset[1], visible: index < 50 };
      });
      positions.forEach(pos => labelPositions.set(pos.id, pos));
    }
    
    let addedCount = 0; let skippedCount = 0;
    dataArray.forEach((item, index) => {
      if (!item[latitudeColumn] || !item[longitudeColumn]) { skippedCount++; return; }
      const lat = parseFloat(item[latitudeColumn]); const lon = parseFloat(item[longitudeColumn]);
      if (isNaN(lat) || isNaN(lon)) { skippedCount++; return; }
      
      let pointColor = completeSymbol.color;
      if (finalRendererType === 'classBreaks' && valueColumn) {
        const pointValue = item[valueColumn];
        if (pointValue !== undefined && pointValue !== null && classBreakInfos && classBreakInfos.length > 0) {
          // Strip common suffixes (s for stories, ft, etc.) before numeric conversion
          let cleanedValue = pointValue;
          if (typeof pointValue === 'string') {
            cleanedValue = pointValue.trim().replace(/s$/i, '').replace(/ft$/i, '').replace(/stories?$/i, '');
          }
          const numValue = Number(cleanedValue);
          if (!isNaN(numValue)) {
            for (const breakInfo of classBreakInfos) {
              if ((breakInfo.minValue === undefined || numValue >= breakInfo.minValue) && (breakInfo.maxValue === undefined || numValue <= breakInfo.maxValue)) {
                pointColor = breakInfo.symbol?.color || completeSymbol.color; break;
              }
            }
          }
        }
      } else if (finalRendererType === 'uniqueValue' || finalRendererType === 'uniqueValueOrSimple') {
        const status = item[finalStatusColumn] || "default";
        // Try exact match first
        pointColor = mergedStatusColors[status];
        // If no exact match, try case-insensitive match
        if (!pointColor) {
          const statusLower = status.toString().toLowerCase();
          const matchedKey = Object.keys(mergedStatusColors).find(key =>
            key.toLowerCase() === statusLower
          );
          pointColor = matchedKey ? mergedStatusColors[matchedKey] : mergedStatusColors.default;
        }
        console.log(`[createPipeLayer] Status: "${status}" -> Color: ${pointColor}`);
      }
      
      try {
        const point = new Point({ x: lon, y: lat, spatialReference: { wkid: 4326 } });
        const pointSymbol = new SimpleMarkerSymbol({ style: completeSymbol.style, size: completeSymbol.size, color: new Color(pointColor), outline: { color: new Color(completeSymbol.outline.color), width: completeSymbol.outline.width } });
        
        let pointTitle = item[labelColumn] || `Point ${index}`;
        const variableParts = [];
        if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) variableParts.push((variable1Prefix || '') + item[variable1Column] + (variable1Text || ''));
        if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) variableParts.push((variable2Prefix || '') + item[variable2Column] + (variable2Text || ''));
        if (variableParts.length > 0 && includeVariables) pointTitle += ' (' + variableParts.join(', ') + ')';

        const fieldInfos = [];
        if (finalStatusColumn && item[finalStatusColumn]) fieldInfos.push({ fieldName: "status", label: finalStatusColumn || "Status" });
        if (valueColumn && item[valueColumn] !== undefined) fieldInfos.push({ fieldName: "value", label: valueColumn || "Value", format: config.valueFormat ? { digitSeparator: true, places: config.valueFormat.decimals || 0, prefix: config.valueFormat.prefix || '', suffix: config.valueFormat.suffix || '' } : null });

        const graphic = new Graphic({
          geometry: point, symbol: pointSymbol,
          attributes: { ...item, OBJECTID: index, displayName: pointTitle, status: item[finalStatusColumn] || "Unknown", value: valueColumn ? item[valueColumn] : null, variable1Text: variable1Text, variable2Text: variable2Text, variable1Prefix: variable1Prefix, variable2Prefix: variable2Prefix, hasCustomFormat: true },
          popupTemplate: new PopupTemplate({ title: "{displayName}", content: fieldInfos.length > 0 ? [{ type: "fields", fieldInfos: fieldInfos }] : "No additional information available" })
        });
        graphicsLayer.add(graphic);
        addedCount++;

        // Only create label graphics if labels are not disabled
        if (!disableLabels) {
          let labelText = item[labelColumn] || `Point ${index}`;
          if (includeVariables) {
            const labelVariableParts = [];
            if (variable1Column && item[variable1Column] !== undefined && item[variable1Column] !== null) labelVariableParts.push((variable1Prefix || '') + item[variable1Column] + (variable1Text || ''));
            if (variable2Column && item[variable2Column] !== undefined && item[variable2Column] !== null) labelVariableParts.push((variable2Prefix || '') + item[variable2Column] + (variable2Text || ''));
            if (labelVariableParts.length > 0) labelText += ' (' + labelVariableParts.join(', ') + ')';
          }

          const labelId = `label-${index}`;
          let labelOffset = { x: 0, y: fontSize + 4, visible: true };
          if (avoidCollisions && labelPositions.has(labelId)) labelOffset = labelPositions.get(labelId);

          // Create offset point for label positioning (geographic coordinates)
          // Vary the initial position based on the index to reduce initial collisions
          // Prioritize left/right placement before moving labels farther away
          // Use appropriate horizontal alignment so labels don't overlap with their points
          const positionPatterns = [
            { latOffset: 0, lonOffset: -0.0001, hAlign: "right" },   // Left (text extends left from anchor)
            { latOffset: 0, lonOffset: 0.0001, hAlign: "left" },     // Right (text extends right from anchor)
            { latOffset: 0.0006, lonOffset: 0, hAlign: "center" },   // Above
            { latOffset: -0.0006, lonOffset: 0, hAlign: "center" },  // Below
            { latOffset: 0.0004, lonOffset: -0.0001, hAlign: "right" }, // Upper left
            { latOffset: -0.0004, lonOffset: -0.0001, hAlign: "right" }, // Lower left
            { latOffset: 0.0004, lonOffset: 0.0001, hAlign: "left" },  // Upper right
            { latOffset: -0.0004, lonOffset: 0.0001, hAlign: "left" } // Lower right
          ];

          const pattern = positionPatterns[index % positionPatterns.length];
          const offsetLatLng = {
            latitude: lat + pattern.latOffset,
            longitude: lon + pattern.lonOffset
          };

          const labelPoint = new Point({
            x: offsetLatLng.longitude,  // ArcGIS uses x for longitude
            y: offsetLatLng.latitude,   // ArcGIS uses y for latitude
            spatialReference: { wkid: 4326 }
          });

          const whiteBackground = labelOptions.whiteBackground || false;
          const showMask = !whiteBackground && (labelOptions.mask !== false); // mask defaults to true
          const textSymbol = new TextSymbol({
            text: labelText,
            font: { size: fontSize, family: fontFamily, weight: labelOptions.bold ? "bold" : "normal" },
            color: new Color([0, 0, 0, 0.9]),
            haloColor: showMask ? new Color([255, 255, 255, 0.9]) : null,
            haloSize: showMask ? 1 : 0,
            backgroundColor: whiteBackground ? [255, 255, 255, 0.9] : null,
            borderLineColor: whiteBackground ? [255, 255, 255, 0.9] : null,
            borderLineSize: whiteBackground ? 4 : 0,
            // NO pixel offsets - using geographic positioning
            xoffset: 0,
            yoffset: 0,
            horizontalAlignment: pattern.hAlign,  // Dynamic alignment based on position
            verticalAlignment: "middle"
          });
          const labelGraphic = new Graphic({
            geometry: labelPoint, symbol: textSymbol,
            attributes: { ...item, OBJECTID: `label-${index}`, parentID: index, isLabel: true, visible: true, variable1Column: variable1Column, variable2Column: variable2Column, variable1Text: variable1Text, variable2Text: variable2Text, variable1Prefix: variable1Prefix, variable2Prefix: variable2Prefix, hasCustomFormat: true, labelText: labelText, baseText: item[labelColumn] || `Point ${index}`, dataPointGeometry: point.clone(), markerSize: completeSymbol.size },
            visible: visibleAtAllZooms
          });
          graphicsLayer.add(labelGraphic);
        }
      } catch (pointError) { console.error(`[createPipeLayer] Error creating point graphic at index ${index}:`, pointError); skippedCount++; }
    });
    
    console.log(`[createPipeLayer] Pipeline layer creation complete: Added ${addedCount} points with labels, skipped ${skippedCount} invalid points for config ${mapConfigId}`);
    
    graphicsLayer.variable1Text = variable1Text;
    graphicsLayer.variable2Text = variable2Text;
    graphicsLayer.variable1Prefix = variable1Prefix;
    graphicsLayer.variable2Prefix = variable2Prefix;
    // graphicsLayer.mapConfigId is already set

    // Ensure mapConfigId is included in the labelFormatInfo
    graphicsLayer.labelFormatInfo = {
      labelColumn,
      variable1Column,
      variable2Column,
      variable1Text,
      variable2Text,
      variable1Prefix,
      variable2Prefix,
      includeVariables,
      mapConfigId: mapConfigId // Explicitly include mapConfigId
    };
    
  } catch (error) {
    console.error("[createPipeLayer] Error during pipeline layer creation:", error);
  }
  
  return graphicsLayer;
};

/**
 * Creates a graphics layer from custom data with dual-value renderer support
 * Handles complex symbol and size mappings based on multiple value columns
 * 
 * @param {Object} config - Configuration object for the custom layer
 * @returns {Promise<GraphicsLayer>} The configured custom graphics layer
 */
export const createGraphicsLayerFromCustomData = async (config) => {
  console.log("[createGraphicsLayerFromCustomData] Received config:", JSON.stringify(config, (k, v) => 
    (k === "data" || k === "customData") ? `[${v?.data?.length || v?.length} items]` : v));

  // Dynamic imports
  const { default: GraphicsLayer } = await import("@arcgis/core/layers/GraphicsLayer");
  const { default: Graphic } = await import("@arcgis/core/Graphic");
  const { default: Point } = await import("@arcgis/core/geometry/Point");
  const { default: SimpleMarkerSymbol } = await import("@arcgis/core/symbols/SimpleMarkerSymbol");
  const { default: TextSymbol } = await import("@arcgis/core/symbols/TextSymbol");
  const { default: Color } = await import("@arcgis/core/Color");
  const { default: PopupTemplate } = await import("@arcgis/core/PopupTemplate");

  const layer = new GraphicsLayer({
    title: config.title || "Custom Data Layer",
    listMode: "hide",
    visualizationType: "custom",
    rendererType: config.rendererType || "simple",
    isVisualizationLayer: true,
  });
  
  let customData = [];
  if (config.customData?.data) {
    customData = config.customData.data;
  } else if (config.data) {
    customData = config.data;
  } else if (config.customData && Array.isArray(config.customData)) {
    customData = config.customData;
  }

  if (!Array.isArray(customData) || customData.length === 0) {
    console.warn("[createGraphicsLayerFromCustomData] No custom data provided.");
    return layer;
  }

  const latCol = config.latitudeColumn || "latitude";
  const lonCol = config.longitudeColumn || "longitude";
  const labelCol = config.labelColumn || "name";
  const var1Col = config.variable1Column;
  const var2Col = config.variable2Column;
  const var1Text = config.variable1Text || "";
  const var2Text = config.variable2Text || "";
  const var1Prefix = config.variable1Prefix || "";
  const var2Prefix = config.variable2Prefix || "";

  const isDualValueRender = config.rendererType === 'custom-dual-value' || !!(config.valueColumn1 && config.valueColumn2);
  const valueCol1 = config.valueColumn1;
  const valueCol2 = config.valueColumn2;

  const colorBreaks = Array.isArray(config.colorClassBreakInfos) ? config.colorClassBreakInfos : [];
  const sizeBreaks = Array.isArray(config.sizeClassBreakInfos) ? config.sizeClassBreakInfos : (Array.isArray(config.sizeInfos) ? config.sizeInfos : []);

  // Define baseSymbolConfig based on rendererType
  let baseSymbolConfig;
  const inputSymbolConfig = config.symbol || {};

  if (isDualValueRender) {
    // For dual-value, base symbol defines outline, style, and min/max for ramps.
    // Fixed color/size are ignored; they come from breaks.
    baseSymbolConfig = {
      type: "simple-marker", // Ensure type is always set
      style: inputSymbolConfig.style || "circle",
      outline: inputSymbolConfig.outline || { color: "#FFFFFF", width: 0 },
      minSize: inputSymbolConfig.minSize || 10, // Crucial for size ramp default
      maxSize: inputSymbolConfig.maxSize || 28,
      // Explicitly DO NOT include 'color' or 'size' for dual-value base
    };
  } else {
    // For simple or single class-break custom maps, a base color/size might be intended.
    baseSymbolConfig = {
      type: "simple-marker", // Ensure type is always set
      style: inputSymbolConfig.style || "circle",
      outline: inputSymbolConfig.outline || { color: "#FFFFFF", width: 0 },
      color: inputSymbolConfig.color || "#FF0000", // Default red for non-dual-value
      size: inputSymbolConfig.size || 10,         // Default size for non-dual-value
    };
  }

  console.log(`[createGraphicsLayerFromCustomData] Processing ${customData.length} points with:`, {
    rendererType: config.rendererType || 'simple',
    isDualValue: isDualValueRender,
    colorColumn: valueCol1 || 'N/A',
    sizeColumn: valueCol2 || 'N/A',
    colorBreaksCount: colorBreaks.length,
    sizeBreaksCount: sizeBreaks.length,
    baseSymbolConfig: baseSymbolConfig // Log the determined baseSymbolConfig
  });

  // Calculate data range for continuous size scaling (when using valueCol2)
  // Use continuous sizing when valueCol2 is set AND (no size breaks OR minSize/maxSize are explicitly set)
  let sizeDataMin = null;
  let sizeDataMax = null;
  const hasExplicitSizeRange = baseSymbolConfig.minSize !== undefined && baseSymbolConfig.maxSize !== undefined;
  const useContinuousSizing = isDualValueRender && valueCol2 && (sizeBreaks.length === 0 || hasExplicitSizeRange);

  if (useContinuousSizing) {
    const sizeValues = customData
      .map(item => parseFloat(item[valueCol2]))
      .filter(val => !isNaN(val));

    if (sizeValues.length > 0) {
      sizeDataMin = Math.min(...sizeValues);
      sizeDataMax = Math.max(...sizeValues);
      console.log(`[createGraphicsLayerFromCustomData] Continuous size scaling enabled for '${valueCol2}': min=${sizeDataMin}, max=${sizeDataMax}, size range=[${baseSymbolConfig.minSize}, ${baseSymbolConfig.maxSize}]`);
    }
  }

  const pointGraphics = [];
  const labelGraphics = [];

  customData.forEach((item, index) => {
    try {
      let lat, lon;
      if (item[latCol] !== undefined && item[lonCol] !== undefined) {
        lat = parseFloat(item[latCol]);
        lon = parseFloat(item[lonCol]);
      } else if (item.geometry && typeof item.geometry.y === "number" && typeof item.geometry.x === "number") {
        lat = item.geometry.y;
        lon = item.geometry.x;
      } else if (item.lat !== undefined && item.lon !== undefined) {
        lat = parseFloat(item.lat);
        lon = parseFloat(item.lon);
      } else if (item.latitude !== undefined && item.longitude !== undefined) {
        lat = parseFloat(item.latitude);
        lon = parseFloat(item.longitude);
      }

      if (isNaN(lat) || isNaN(lon)) {
        console.warn(`[createGraphicsLayerFromCustomData] Skipping item ${index} due to invalid coordinates.`);
        return;
      }

      const point = new Point({ 
        x: lon,  // ArcGIS uses x for longitude
        y: lat,  // ArcGIS uses y for latitude
        spatialReference: { wkid: 4326 } 
      });

      // Default finalColorHex and finalSize
      // Use first color break as fallback instead of grey
      let finalColorHex = isDualValueRender
        ? (colorBreaks.length > 0 && colorBreaks[0]?.symbol?.color ? colorBreaks[0].symbol.color : "#FF0000")
        : (baseSymbolConfig.color || "#FF0000");
      let finalSize = isDualValueRender
        ? (baseSymbolConfig.minSize || 10) // Default to minSize for unmatched dual-value
        : (parseFloat(baseSymbolConfig.size || 10));

      if (isDualValueRender) {
        if (valueCol1 && colorBreaks.length > 0 && item[valueCol1] !== undefined) {
          // Check if this is a text field (first break has isTextField or isCategorical flag)
          const isTextFieldBreaks = colorBreaks.length > 0 && (colorBreaks[0].isTextField || colorBreaks[0].isCategorical);

          if (isTextFieldBreaks) {
            // Text field matching - match by value property
            const textValue = String(item[valueCol1]);
            let matchedColor = false;

            for (const breakInfo of colorBreaks) {
              if (breakInfo.value === textValue) {
                if (breakInfo.symbol && breakInfo.symbol.color) {
                  finalColorHex = breakInfo.symbol.color;
                  matchedColor = true;
                  break;
                }
              }
            }

            if (!matchedColor) {
              console.warn(`[createGraphicsLayerFromCustomData] Text value "${textValue}" for ${valueCol1} could not be matched to any break. Using fallback color.`);
            }
          } else {
            // Numeric field matching - match by range
            const val1 = parseFloat(item[valueCol1]);
            if (!isNaN(val1)) {
              let matchedColor = false;
              let closestBreak = null;
              let closestDistance = Infinity;

              // First pass: try exact match
              for (const breakInfo of colorBreaks) {
                const minVal = breakInfo.minValue !== undefined ? parseFloat(breakInfo.minValue) : -Infinity;
                const maxVal = breakInfo.maxValue !== undefined ? parseFloat(breakInfo.maxValue) : Infinity;
                if (val1 >= minVal && val1 <= maxVal) {
                  if (breakInfo.symbol && breakInfo.symbol.color) {
                    finalColorHex = breakInfo.symbol.color;
                    matchedColor = true;
                    break;
                  }
                }

                // Track closest break for fallback
                const breakMidpoint = (minVal + maxVal) / 2;
                const distance = Math.abs(val1 - breakMidpoint);
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestBreak = breakInfo;
                }
              }

              // If no exact match, use closest break (handles gaps in break ranges)
              if (!matchedColor && closestBreak && closestBreak.symbol && closestBreak.symbol.color) {
                finalColorHex = closestBreak.symbol.color;
                matchedColor = true;
                console.log(`[createGraphicsLayerFromCustomData] Value ${val1} for ${valueCol1} fell in gap, using closest break`);
              }

              // If still no match, warn and use fallback
              if (!matchedColor) {
                console.warn(`[createGraphicsLayerFromCustomData] Value ${val1} for ${valueCol1} could not be matched to any break. Using fallback color.`);
              }
            }
          }
        }
        
        // Handle size column (valueCol2)
        if (valueCol2 && item[valueCol2] !== undefined) {
          const val2 = parseFloat(item[valueCol2]);
          if (!isNaN(val2)) {
            // Prefer continuous size scaling when minSize/maxSize are explicitly set
            if (useContinuousSizing && sizeDataMin !== null && sizeDataMax !== null) {
              const minSize = baseSymbolConfig.minSize || 6;
              const maxSize = baseSymbolConfig.maxSize || 24;

              // Handle edge case where all values are the same
              if (sizeDataMin === sizeDataMax) {
                finalSize = (minSize + maxSize) / 2;
              } else {
                // Square root scaling: better visual perception and spreads high values
                // Linear interpolation clusters top values at max size; sqrt distributes them
                const normalized = (val2 - sizeDataMin) / (sizeDataMax - sizeDataMin);
                const sqrtNormalized = Math.sqrt(normalized);
                finalSize = minSize + (sqrtNormalized * (maxSize - minSize));
              }
            }
            // Fallback to discrete size breaks if continuous sizing not enabled
            else if (sizeBreaks.length > 0) {
              for (const sizeBreak of sizeBreaks) {
                const minVal = sizeBreak.minValue !== undefined ? parseFloat(sizeBreak.minValue) : -Infinity;
                const maxVal = sizeBreak.maxValue !== undefined ? parseFloat(sizeBreak.maxValue) : Infinity;
                if (val2 >= minVal && val2 <= maxVal) {
                  if (sizeBreak.size !== undefined) {
                    finalSize = parseFloat(sizeBreak.size);
                    break;
                  }
                }
              }
            }
          }
        }
      } 
      else if (config.classBreakInfos && config.classBreakInfos.length > 0 && config.field) {
        // Check if this is a text field (first break has isTextField or isCategorical flag)
        const isTextFieldBreaks = config.classBreakInfos.length > 0 && (config.classBreakInfos[0].isTextField || config.classBreakInfos[0].isCategorical);

        if (isTextFieldBreaks) {
          // Text field matching - match by value property
          const textValue = String(item[config.field]);
          for (const classBreak of config.classBreakInfos) {
            if (classBreak.value === textValue) {
              if (classBreak.symbol) {
                finalColorHex = classBreak.symbol.color || baseSymbolConfig.color || "#FF0000";
                finalSize = parseFloat(classBreak.symbol.size || baseSymbolConfig.size || 10);
                break;
              }
            }
          }
        } else {
          // Numeric field matching
          const value = parseFloat(item[config.field]);
          if (!isNaN(value)) {
            for (const classBreak of config.classBreakInfos) {
              const minVal = classBreak.minValue !== undefined ? parseFloat(classBreak.minValue) : -Infinity;
              const maxVal = classBreak.maxValue !== undefined ? parseFloat(classBreak.maxValue) : Infinity;
              if (value >= minVal && value <= maxVal) {
                if (classBreak.symbol) {
                  finalColorHex = classBreak.symbol.color || baseSymbolConfig.color || "#FF0000";
                  finalSize = parseFloat(classBreak.symbol.size || baseSymbolConfig.size || 10);
                  break;
                }
              }
            }
          }
        }
      }

      // Apply dot transparency if specified (config.dotTransparency is 0-1, where 1 is fully opaque)
      const dotTransparency = config.dotTransparency !== undefined ? config.dotTransparency : 1;
      let finalColor = new Color(finalColorHex);
      // Apply transparency (Color.a is 0-1 where 1 is fully opaque)
      finalColor.a = dotTransparency;

      const pointSymbol = new SimpleMarkerSymbol({
        style: baseSymbolConfig.style || "circle",
        color: finalColor,
        size: finalSize,
        outline: {
          color: new Color(baseSymbolConfig.outline?.color || "#FFFFFF"),
          width: parseFloat(baseSymbolConfig.outline?.width ?? 0)
        }
      });

      let displayLabel = String(item[labelCol] || `Point ${index + 1}`);
      if (config.labelOptions?.includeVariables !== false) {
        if (var1Col && item[var1Col] !== undefined) {
          displayLabel += `, ${item[var1Col]}${var1Text || ''}`;
        }
        if (var2Col && item[var2Col] !== undefined) {
          displayLabel += ` / ${item[var2Col]}${var2Text || ''}`;
        }
      }

      const attributes = {
        ...item,
        _internalId: `custom-${index}`,
        labelText: displayLabel,
        isCustomPoint: true,
        FEATURE_TYPE: "custom",
        parentID: `custom-${index}`,
        rendererType: config.rendererType,
        [valueCol1]: item[valueCol1],
        [valueCol2]: item[valueCol2],
        [labelCol]: item[labelCol] || `Point ${index + 1}`
      };

      const popupTemplate = new PopupTemplate({
        title: attributes.labelText,
        content: [{
          type: "fields",
          fieldInfos: Object.keys(item)
            .filter(key => !['geometry', '_internalId'].includes(key))
            .map(key => ({ fieldName: key, label: key }))
        }]
      });

      pointGraphics.push(new Graphic({ 
        geometry: point, 
        symbol: pointSymbol, 
        attributes, 
        popupTemplate 
      }));

      // Check if labels should be disabled
      const disableLabels = config?.hasNoLabels || config?.hideAllLabels || config.labelOptions?.showLabels === false;
      if (!disableLabels) {
        const labelFontSize = config.labelOptions?.fontSize || 10;
        // Create offset point for label positioning (geographic coordinates)
        // Vary the initial position based on the index to reduce initial collisions
        // Prioritize left/right placement before moving labels farther away
        // Use appropriate horizontal alignment so labels don't overlap with their points
        const positionPatterns = [
          { latOffset: 0, lonOffset: -0.0001, hAlign: "right" },   // Left (text extends left from anchor)
          { latOffset: 0, lonOffset: 0.0001, hAlign: "left" },     // Right (text extends right from anchor)
          { latOffset: 0.0006, lonOffset: 0, hAlign: "center" },   // Above
          { latOffset: -0.0006, lonOffset: 0, hAlign: "center" },  // Below
          { latOffset: 0.0004, lonOffset: -0.0001, hAlign: "right" }, // Upper left
          { latOffset: -0.0004, lonOffset: -0.0001, hAlign: "right" }, // Lower left
          { latOffset: 0.0004, lonOffset: 0.0001, hAlign: "left" },  // Upper right
          { latOffset: -0.0004, lonOffset: 0.0001, hAlign: "left" } // Lower right
        ];

        const pattern = positionPatterns[index % positionPatterns.length];
        const offsetLatLng = {
          latitude: lat + pattern.latOffset,
          longitude: lon + pattern.lonOffset
        };

        const labelPoint = new Point({
          x: offsetLatLng.longitude,  // ArcGIS uses x for longitude
          y: offsetLatLng.latitude,   // ArcGIS uses y for latitude
          spatialReference: { wkid: 4326 }
        });

        const whiteBackground = config.labelOptions?.whiteBackground || false;
        const showMask = !whiteBackground && (config.labelOptions?.mask !== false); // mask defaults to true
        const labelSymbol = new TextSymbol({
          text: displayLabel,
          color: new Color(config.labelOptions?.color || "#000000"),
          haloColor: showMask ? new Color(config.labelOptions?.haloColor || "#FFFFFF") : null,
          haloSize: showMask ? (config.labelOptions?.haloSize || 1) : 0,
          backgroundColor: whiteBackground ? [255, 255, 255, 0.9] : null,
          borderLineColor: whiteBackground ? [255, 255, 255, 0.9] : null,
          borderLineSize: whiteBackground ? 4 : 0,
          font: {
            size: labelFontSize,
            family: config.labelOptions?.fontFamily || "sans-serif",
            weight: config.labelOptions?.bold ? "bold" : "normal"
          },
          // NO pixel offsets - using geographic positioning
          xoffset: 0,
          yoffset: 0,
          horizontalAlignment: pattern.hAlign,  // Dynamic alignment based on position
          verticalAlignment: "middle"
        });
        
        labelGraphics.push(new Graphic({
          geometry: labelPoint,
          symbol: labelSymbol,
          attributes: {
            ...attributes,
            isLabel: true,
            FEATURE_TYPE: "label",
            layerId: layer.id,
            parentID: `parent-custom-${index}`,
            dataPointGeometry: point.clone(),  // Store original data point location for label collision detection
            markerSize: finalSize  // Store marker size for collision boundary scaling
          }
        }));
      }
    } catch (error) {
      console.error(`[createGraphicsLayerFromCustomData] Error processing item ${index}:`, error, item);
    }
  });

  if (pointGraphics.length > 0) {
    layer.addMany(pointGraphics);
  }
  if (labelGraphics.length > 0) {
    layer.addMany(labelGraphics);
  }
  
  layer.customConfig = {
    rendererType: config.rendererType,
    valueColumn1: valueCol1,
    valueColumn2: valueCol2,
    colorBreaks: colorBreaks,
    sizeBreaks: sizeBreaks,
    isDualValue: isDualValueRender,
    pointCount: pointGraphics.length,
    labelCount: labelGraphics.length
  };
  
  console.log(`[createGraphicsLayerFromCustomData] Successfully created layer with ${pointGraphics.length} points, ${labelGraphics.length} labels using ${isDualValueRender ? 'custom-dual-value' : (config.rendererType || 'simple')} renderer`);
  
  return layer;
};

/**
 * Enhanced createRenderer function with comprehensive type handling and auto-detection
 * Supports both class-breaks and dot-density renderers with robust fallback logic
 * 
 * @param {Object} config - Configuration object containing renderer settings
 * @param {Object} areaType - Area type information for defaults (optional)
 * @returns {Promise<Object|null>} - ArcGIS renderer instance or null if creation fails
 */
const createRenderer = async (config, areaType = null) => {
  console.log("[createRenderer] Creating renderer with config:", {
    originalType: config?.type,
    field: config?.field,
    hasClassBreaks: !!(config?.classBreakInfos),
    hasAttributes: !!(config?.attributes),
    hasDotValue: config?.dotValue !== undefined,
    configKeys: config ? Object.keys(config) : [],
    configStructure: config ? JSON.stringify(config, null, 2).substring(0, 500) + '...' : 'null'
  });
  
  // Validate input config
  if (!config || typeof config !== 'object') {
    console.error("[createRenderer] Invalid or missing config object:", config);
    return null;
  }

  try {
    // Normalize the renderer type with fallback detection
    const normalizedType = normalizeRendererType(config.type, config);
    
    if (!normalizedType) {
      console.error("[createRenderer] Unable to determine renderer type from config:", config);
      return null;
    }
    
    console.log(`[createRenderer] Type normalized: '${config.type}' → '${normalizedType}'`);

    // Import required ArcGIS modules dynamically
    const [
      { default: ClassBreaksRenderer },
      { default: DotDensityRenderer },
      { default: SimpleFillSymbol },
      { default: SimpleLineSymbol },
      { default: Color }
    ] = await Promise.all([
      import("@arcgis/core/renderers/ClassBreaksRenderer"),
      import("@arcgis/core/renderers/DotDensityRenderer"),
      import("@arcgis/core/symbols/SimpleFillSymbol"),
      import("@arcgis/core/symbols/SimpleLineSymbol"),
      import("@arcgis/core/Color")
    ]);

    // Create Class Breaks Renderer (for heatmaps, choropleth maps)
    if (normalizedType === 'class-breaks') {
      console.log("[createRenderer] Building ClassBreaksRenderer");
      
      // Validate required properties
      // For comps and 1varmap, prefer valueColumn over field if field seems to be mapType
      const field = config.field || config.valueField || config.valueColumn || config.classificationField;
      if (!field) {
        console.error("[createRenderer] Missing required 'field' property for class-breaks renderer");
        return null;
      }

      // Enhanced class break info extraction with multiple fallback paths
      let classBreakInfos = null;
      
      // Method 1: Direct classBreakInfos property (array)
      if (config.classBreakInfos && Array.isArray(config.classBreakInfos)) {
        classBreakInfos = config.classBreakInfos;
        console.log("[createRenderer] Found classBreakInfos via direct property (array)");
      }
      // Method 1b: Nested classBreakInfos structure (object containing classBreakInfos array)
      else if (config.classBreakInfos && typeof config.classBreakInfos === 'object' && 
               config.classBreakInfos.classBreakInfos && Array.isArray(config.classBreakInfos.classBreakInfos)) {
        classBreakInfos = config.classBreakInfos.classBreakInfos;
        console.log("[createRenderer] Found classBreakInfos via nested classBreakInfos.classBreakInfos property");
      }
      // Method 2: Alternative property names
      else if (config.breaks && Array.isArray(config.breaks)) {
        classBreakInfos = config.breaks;
        console.log("[createRenderer] Found classBreakInfos via 'breaks' property");
      }
      else if (config.classBreaks && Array.isArray(config.classBreaks)) {
        classBreakInfos = config.classBreaks;
        console.log("[createRenderer] Found classBreakInfos via 'classBreaks' property");
      }
      // Method 3: Check if config itself is an array of breaks
      else if (Array.isArray(config) && config.length > 0 && config[0].symbol) {
        classBreakInfos = config;
        console.log("[createRenderer] Config itself appears to be classBreakInfos array");
      }
      // Method 4: Check for nested structure (common in complex configs)
      else if (config.renderer && config.renderer.classBreakInfos && Array.isArray(config.renderer.classBreakInfos)) {
        classBreakInfos = config.renderer.classBreakInfos;
        console.log("[createRenderer] Found classBreakInfos via nested renderer property");
      }
      // Method 5: Check for layerConfiguration structure
      else if (config.layerConfiguration && config.layerConfiguration.classBreakInfos && Array.isArray(config.layerConfiguration.classBreakInfos)) {
        classBreakInfos = config.layerConfiguration.classBreakInfos;
        console.log("[createRenderer] Found classBreakInfos via layerConfiguration property");
      }
      // Method 6: Deep nested check - sometimes the structure can be deeply nested
      else if (config.classBreakInfos && typeof config.classBreakInfos === 'object') {
        // Try to find any array property within the classBreakInfos object
        const possibleArrays = Object.values(config.classBreakInfos).filter(Array.isArray);
        if (possibleArrays.length > 0 && possibleArrays[0].length > 0 && possibleArrays[0][0].symbol) {
          classBreakInfos = possibleArrays[0];
          console.log("[createRenderer] Found classBreakInfos via deep nested search within classBreakInfos object");
        }
      }

      // Final validation of class break infos
      if (!classBreakInfos || !Array.isArray(classBreakInfos) || classBreakInfos.length === 0) {
        console.error("[createRenderer] Missing or invalid 'classBreakInfos' for class-breaks renderer", {
          configKeys: Object.keys(config),
          hasClassBreakInfos: !!config.classBreakInfos,
          classBreakInfosType: typeof config.classBreakInfos,
          classBreakInfosLength: Array.isArray(config.classBreakInfos) ? config.classBreakInfos.length : 'not array',
          hasBreaks: !!config.breaks,
          breaksType: typeof config.breaks,
          breaksLength: Array.isArray(config.breaks) ? config.breaks.length : 'not array',
          configStructureSample: JSON.stringify(config, null, 2).substring(0, 300)
        });
        return null;
      }

      console.log(`[createRenderer] Successfully extracted ${classBreakInfos.length} class break infos`);

      // Process and validate class break infos
      const defaultColors = createDefaultClassBreakSymbols(classBreakInfos.length);
      const processedBreakInfos = classBreakInfos.map((breakInfo, index) => {
        // Handle potential data format variations
        let processedBreak = breakInfo;
        
        // If breakInfo is not an object, try to construct one
        if (typeof breakInfo !== 'object' || breakInfo === null) {
          console.warn(`[createRenderer] Break info at index ${index} is not an object:`, breakInfo);
          processedBreak = { minValue: 0, maxValue: 100, label: `Class ${index + 1}` };
        }

        // Ensure min/max values are properly set with enhanced fallback logic
        let minValue = processedBreak.minValue ?? processedBreak.min ?? processedBreak.classMinValue;
        let maxValue = processedBreak.maxValue ?? processedBreak.max ?? processedBreak.classMaxValue;
        
        // Handle special cases for first and last breaks
        if (minValue === null || minValue === undefined) {
          minValue = index === 0 ? 0 : (classBreakInfos[index - 1]?.maxValue ?? index * 10);
        }
        if (maxValue === null || maxValue === undefined) {
          maxValue = minValue + 10; // Default range of 10
        }

        // Create or process symbol with enhanced error handling
        let symbol;
        try {
          if (processedBreak.symbol && typeof processedBreak.symbol === 'object') {
            // Use existing symbol structure
            const symbolColor = processedBreak.symbol.color || defaultColors[index] || [128, 128, 128, 0.8];
            const outlineColor = processedBreak.symbol.outline?.color || [255, 255, 255, 0.5];
            const outlineWidth = processedBreak.symbol.outline?.width ?? 0.5;
            
            symbol = new SimpleFillSymbol({
              color: new Color(symbolColor),
              style: processedBreak.symbol.style || "solid",
              outline: new SimpleLineSymbol({
                color: new Color(outlineColor),
                width: outlineWidth,
                style: "solid"
              })
            });
          } else {
            // Create default symbol with TCG color palette
            const colorArray = defaultColors[index] || [128, 128, 128, 0.8];
            
            symbol = new SimpleFillSymbol({
              color: new Color(colorArray),
              style: "solid",
              outline: new SimpleLineSymbol({
                color: new Color([255, 255, 255, 0.5]),
                width: 0.5,
                style: "solid"
              })
            });
          }
        } catch (symbolError) {
          console.error(`[createRenderer] Error creating symbol for break ${index}:`, symbolError);
          // Fallback to basic symbol
          const colorArray = defaultColors[index] || [128, 128, 128, 0.8];
          symbol = new SimpleFillSymbol({
            color: new Color(colorArray),
            style: "solid",
            outline: new SimpleLineSymbol({
              color: new Color([255, 255, 255, 0.5]),
              width: 0.5,
              style: "solid"
            })
          });
        }

        // Generate label if not provided with enhanced fallback logic
        let label = processedBreak.label || processedBreak.description || processedBreak.text;
        if (!label) {
          // Create a formatted label based on values
          if (minValue !== null && maxValue !== null) {
            // Format numbers for better readability
            const formatNumber = (num) => {
              if (Number.isInteger(num)) {
                return num.toLocaleString();
              } else {
                return num.toFixed(1);
              }
            };
            
            if (index === 0) {
              label = `Less than ${formatNumber(maxValue)}`;
            } else if (index === classBreakInfos.length - 1) {
              label = `${formatNumber(minValue)} or more`;
            } else {
              // Only show range if min and max are different, otherwise just show single value
              const formattedMin = formatNumber(minValue);
              const formattedMax = formatNumber(maxValue);
              label = formattedMin === formattedMax ? formattedMin : `${formattedMin} - ${formattedMax}`;
            }
          } else {
            label = `Class ${index + 1}`;
          }
        }

        return {
          minValue: minValue,
          maxValue: maxValue,
          symbol: symbol,
          label: label
        };
      });

      // Filter out any invalid break infos
      const validBreakInfos = processedBreakInfos.filter(breakInfo => 
        breakInfo && breakInfo.symbol && 
        typeof breakInfo.minValue === 'number' && 
        typeof breakInfo.maxValue === 'number'
      );

      if (validBreakInfos.length === 0) {
        console.error("[createRenderer] No valid break infos after processing");
        return null;
      }

      console.log(`[createRenderer] Processed ${validBreakInfos.length} valid break infos from ${classBreakInfos.length} original breaks`);

      // Create and return the renderer
      // Determine legend title - use custom mapping for comp maps
      let legendTitle = config.legendOptions?.title || config.legendTitle;

      if (!legendTitle) {
        // Check if this is a comp map by looking at the config type
        const isCompMap = config.type === 'comp' || config.type === 'comps';
        if (isCompMap && field) {
          legendTitle = getCompMapLegendTitle(field);
          console.log(`[createRenderer] Using comp map legend title: "${legendTitle}" for field: ${field}`);
        } else {
          legendTitle = config.title || field || "Classification";
        }
      } else {
        console.log(`[createRenderer] Using provided legend title: "${legendTitle}"`);
      }

      const renderer = new ClassBreaksRenderer({
        field: field,
        classBreakInfos: validBreakInfos,
        legendOptions: {
          title: legendTitle
        }
      });

      console.log(`[createRenderer] ClassBreaksRenderer created successfully with ${validBreakInfos.length} breaks`);
      return renderer;
    }

    // Create Dot Density Renderer
    else if (normalizedType === 'dot-density') {
      console.log("[createRenderer] Building DotDensityRenderer");
      
      // Enhanced attribute extraction with multiple fallback paths
      let attributes = null;
      
      if (config.attributes && Array.isArray(config.attributes)) {
        attributes = config.attributes;
      } else if (config.fields && Array.isArray(config.fields)) {
        attributes = config.fields;
      } else if (config.dotAttributes && Array.isArray(config.dotAttributes)) {
        attributes = config.dotAttributes;
      } else if (config.renderer && config.renderer.attributes && Array.isArray(config.renderer.attributes)) {
        attributes = config.renderer.attributes;
      }

      if (!attributes || !Array.isArray(attributes) || attributes.length === 0) {
        console.error("[createRenderer] Missing or invalid 'attributes' array for dot-density renderer", {
          configKeys: Object.keys(config),
          hasAttributes: !!config.attributes,
          attributesType: typeof config.attributes,
          attributesLength: Array.isArray(config.attributes) ? config.attributes.length : 'not array'
        });
        return null;
      }

      // Process attributes with default colors and enhanced validation
      const defaultColors = createDefaultDotDensityColors(attributes.length);
      const processedAttributes = attributes.map((attr, index) => {
        // Handle both object and string attribute formats
        const field = typeof attr === 'string' ? attr : (attr.field || attr.name || attr.fieldName);
        const label = typeof attr === 'string' ? field : (attr.label || attr.description || attr.displayName || field);
        const color = typeof attr === 'object' && attr.color ? attr.color : defaultColors[index];
        
        if (!field) {
          console.warn(`[createRenderer] Attribute at index ${index} missing field name:`, attr);
          return null;
        }

        return {
          field: field,
          color: new Color(color),
          label: label
        };
      }).filter(attr => attr !== null); // Remove invalid attributes

      if (processedAttributes.length === 0) {
        console.error("[createRenderer] No valid attributes found for dot-density renderer");
        return null;
      }

      // Set up renderer parameters with defaults based on area type
      const getDefaultDotValueByAreaType = (areaTypeVal) => {
        switch (areaTypeVal) {
          case 10: return 5000;   // State
          case 11: return 500;    // County
          case 4:  return 1000;   // CBSA
          case 8:  return 100;    // Place
          case 9:  return 100;    // Zip Code
          case 12: return 10;     // Census Tract
          case 13: return 5;      // Block Group
          default: return 100;
        }
      };
      const dotValue = config.dotValue || config.valuePerDot || config.dotsPerUnit ||
                      getDefaultDotValueByAreaType(areaType?.value);
      const dotSize = config.dotSize || config.size || 5;

      // Create outline if specified
      let outline = null;
      if (config.outline && typeof config.outline === 'object') {
        outline = new SimpleLineSymbol({
          color: new Color(config.outline.color || [128, 128, 128, 0.3]),
          width: config.outline.width || 0.5,
          style: config.outline.style || "solid"
        });
      }

      // Create and return the renderer
      const renderer = new DotDensityRenderer({
        dotValue: dotValue,
        dotSize: dotSize,
        dotBlendingEnabled: false,  // Disable blending to ensure perfect circles
        attributes: processedAttributes,
        outline: outline,
        seed: 1,  // Fixed seed ensures consistent dot placement across zoom levels
        legendOptions: {
          unit: config.legendOptions?.unit ||
                config.unit ||
                config.valueUnit ||
                "people"
        }
      });

      console.log(`[createRenderer] DotDensityRenderer created successfully with ${processedAttributes.length} attributes`);
      return renderer;
    }

    // Handle unexpected renderer types
    else {
      console.error(`[createRenderer] Unsupported normalized renderer type: '${normalizedType}'. Expected 'class-breaks' or 'dot-density'.`);
      
      // Final fallback attempt - try to auto-detect and recurse with better config
      const hasBreakStructure = config.classBreakInfos || config.breaks || config.classBreaks || 
                               (Array.isArray(config) && config[0]?.symbol);
      const hasAttributeStructure = config.attributes || config.fields || config.dotAttributes || 
                                   config.dotValue !== undefined;

      if (hasBreakStructure) {
        console.log("[createRenderer] Fallback: Attempting class-breaks based on break structure detection");
        return createRenderer({ ...config, type: 'class-breaks' }, areaType);
      } else if (hasAttributeStructure) {
        console.log("[createRenderer] Fallback: Attempting dot-density based on attribute structure detection");
        return createRenderer({ ...config, type: 'dot-density' }, areaType);
      }
      
      return null;
    }

  } catch (error) {
    console.error("[createRenderer] Critical error during renderer creation:", {
      error: error.message,
      stack: error.stack,
      config: config,
      configKeys: config ? Object.keys(config) : [],
      configType: typeof config
    });
    return null;
  }
};

// ============================================================
// WATER BODY CLIPPING FOR DOT DENSITY MAPS
// ============================================================

// USA Water Bodies service URL (Living Atlas)
const USA_WATER_BODIES_URL = "https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Detailed_Water_Bodies/FeatureServer/0";

/**
 * Queries water bodies that intersect with the given extent
 * Returns an array of water body polygon geometries
 *
 * @param {Extent} extent - The extent to query water bodies within
 * @param {Object} spatialReference - Spatial reference for the query
 * @returns {Promise<Array<Polygon>>} Array of water body geometries
 */
async function queryWaterBodies(extent, spatialReference) {
  console.log(`[queryWaterBodies] Querying water bodies for extent`);

  // Create a timeout promise to prevent hanging
  const timeoutMs = 10000; // 10 second timeout
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Water body query timed out')), timeoutMs);
  });

  try {
    const queryPromise = (async () => {
      const waterLayer = new FeatureLayer({
        url: USA_WATER_BODIES_URL
      });
      await waterLayer.load();

      const waterQuery = new Query();
      waterQuery.geometry = extent;
      waterQuery.spatialRelationship = "intersects";
      waterQuery.returnGeometry = true;
      waterQuery.outFields = ["OBJECTID", "NAME", "FTYPE"];
      // Get all water bodies - rivers, lakes, reservoirs, etc.
      waterQuery.where = "1=1";

      return await waterLayer.queryFeatures(waterQuery);
    })();

    const result = await Promise.race([queryPromise, timeoutPromise]);

    if (result.features && result.features.length > 0) {
      console.log(`[queryWaterBodies] Found ${result.features.length} water bodies`);
      return result.features.map(f => f.geometry);
    }

    console.log(`[queryWaterBodies] No water bodies found in extent`);
    return [];
  } catch (err) {
    console.warn(`[queryWaterBodies] Failed to query water bodies:`, err.message);
    return [];
  }
}

/**
 * OPTIMIZED: Clips water bodies from a polygon using a pre-unioned water geometry
 *
 * @param {Polygon} polygon - The original polygon
 * @param {Polygon} unionedWaterGeometry - Single unioned water geometry (or null)
 * @returns {Polygon} The polygon with water areas removed
 */
function clipWaterFromPolygon(polygon, unionedWaterGeometry) {
  if (!unionedWaterGeometry) {
    return polygon;
  }

  try {
    // OPTIMIZATION: Single difference operation instead of iterating over each water body
    if (geometryEngine.intersects(polygon, unionedWaterGeometry)) {
      const result = geometryEngine.difference(polygon, unionedWaterGeometry);
      if (result && result.rings && result.rings.length > 0) {
        return result;
      }
    }
  } catch {
    // Return original on error
  }

  return polygon;
}

/**
 * OPTIMIZED: Clips water bodies from all polygons in the enriched graphics array
 * Uses a single unioned water geometry for efficient clipping
 *
 * @param {Array<Graphic>} enrichedGraphics - Array of graphics with polygon geometries
 * @returns {Promise<Array<Graphic>>} Graphics with water-clipped geometries
 */
async function clipWaterBodiesFromGraphics(enrichedGraphics) {
  if (!enrichedGraphics || enrichedGraphics.length === 0) {
    return enrichedGraphics;
  }

  const startTime = performance.now();

  try {
    // Calculate combined extent of all polygons
    const allGeometries = enrichedGraphics.map(g => g.geometry).filter(Boolean);
    if (allGeometries.length === 0) {
      return enrichedGraphics;
    }

    const unionedExtent = geometryEngine.union(allGeometries);
    const extent = unionedExtent?.extent;

    if (!extent) {
      return enrichedGraphics;
    }

    // Query water bodies within the extent
    const waterGeometries = await queryWaterBodies(extent, allGeometries[0].spatialReference);

    if (waterGeometries.length === 0) {
      return enrichedGraphics;
    }

    // OPTIMIZATION: Union all water bodies into a single geometry ONCE
    // This allows us to do one difference operation per polygon instead of N operations
    let unionedWater = null;
    try {
      if (waterGeometries.length === 1) {
        unionedWater = waterGeometries[0];
      } else {
        unionedWater = geometryEngine.union(waterGeometries);
      }
    } catch {
      // If union fails, skip water clipping
      console.warn(`[clipWaterBodiesFromGraphics] Water union failed, skipping clipping`);
      return enrichedGraphics;
    }

    // Clip water from each polygon using the pre-unioned water geometry
    const clippedGraphics = enrichedGraphics.map((graphic) => {
      if (!graphic.geometry) return graphic;

      const clippedGeometry = clipWaterFromPolygon(graphic.geometry, unionedWater);

      return new Graphic({
        geometry: clippedGeometry || graphic.geometry,
        attributes: { ...graphic.attributes }
      });
    });

    const elapsed = (performance.now() - startTime).toFixed(0);
    console.log(`[clipWaterBodiesFromGraphics] Clipped ${waterGeometries.length} water bodies in ${elapsed}ms`);
    return clippedGraphics;

  } catch (err) {
    console.error(`[clipWaterBodiesFromGraphics] Error:`, err);
    return enrichedGraphics;
  }
}

/**
 * OPTIMIZED: Generates random points within a polygon using grid-based generation with batch containment checks
 * Uses a grid approach with jitter for better performance than pure rejection sampling
 * Batch-checks points using Multipoint geometry to minimize expensive geometry operations
 *
 * @param {Polygon} polygon - The polygon geometry to generate points within
 * @param {number} numPoints - Number of points to generate
 * @param {number} seed - Random seed for reproducible results
 * @param {string} polygonName - Name for logging (optional)
 * @param {Polygon} preBufferedPolygon - Optional pre-buffered polygon to skip buffer computation
 * @returns {Array<Point>} Array of Point geometries within the polygon
 */
function generatePointsInPolygon(polygon, numPoints, seed = 1, _polygonName = 'unknown', preBufferedPolygon = null) {
  if (!polygon || !polygon.extent || numPoints <= 0) {
    return [];
  }

  // Use pre-buffered polygon if provided, otherwise buffer once
  let testPolygon = preBufferedPolygon || polygon;
  if (!preBufferedPolygon) {
    try {
      const bufferedPolygon = geometryEngine.buffer(polygon, -100, "meters");
      if (bufferedPolygon && bufferedPolygon.rings && bufferedPolygon.rings.length > 0) {
        testPolygon = bufferedPolygon;
      }
    } catch {
      // Use original polygon if buffer fails
    }
  }

  const extent = polygon.extent;
  const xMin = extent.xmin;
  const xMax = extent.xmax;
  const yMin = extent.ymin;
  const yMax = extent.ymax;
  const width = xMax - xMin;
  const height = yMax - yMin;

  // Fast seeded random number generator (xorshift for speed)
  let s = seed;
  const seededRandom = () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) / 4294967296);
  };

  const points = [];
  const spatialRef = polygon.spatialReference;

  // OPTIMIZATION: Use grid-based generation with batch containment checking
  // Generate candidate points in batches of 500-1000 for efficient geometry checks
  const BATCH_SIZE = 500;
  const maxTotalAttempts = numPoints * 15;
  let totalAttempts = 0;

  while (points.length < numPoints && totalAttempts < maxTotalAttempts) {
    // Generate a batch of candidate coordinates
    const batchNeeded = Math.min(BATCH_SIZE, (numPoints - points.length) * 3);
    const candidateCoords = [];

    for (let i = 0; i < batchNeeded && totalAttempts < maxTotalAttempts; i++) {
      totalAttempts++;
      const x = xMin + (seededRandom() * width);
      const y = yMin + (seededRandom() * height);
      candidateCoords.push([x, y]);
    }

    if (candidateCoords.length === 0) break;

    // OPTIMIZATION: Batch check using Multipoint geometry - much faster than individual checks
    try {
      const multiPoint = new Multipoint({
        points: candidateCoords,
        spatialReference: spatialRef
      });

      // Use intersection to get only points inside the polygon
      const insideMultipoint = geometryEngine.intersect(multiPoint, testPolygon);

      if (insideMultipoint && insideMultipoint.points) {
        // Extract points from the intersection result
        const insideCoords = insideMultipoint.points;
        for (let i = 0; i < insideCoords.length && points.length < numPoints; i++) {
          points.push(new Point({
            x: insideCoords[i][0],
            y: insideCoords[i][1],
            spatialReference: spatialRef
          }));
        }
      }
    } catch {
      // Fallback to individual checks if batch fails (rare edge cases)
      for (let i = 0; i < candidateCoords.length && points.length < numPoints; i++) {
        const pt = new Point({
          x: candidateCoords[i][0],
          y: candidateCoords[i][1],
          spatialReference: spatialRef
        });
        if (geometryEngine.contains(testPolygon, pt)) {
          points.push(pt);
        }
      }
    }
  }

  return points;
}

/**
 * Creates a custom dot density layer using generated points instead of Esri's DotDensityRenderer
 * This approach guarantees dots are placed only within actual polygon boundaries
 * Clips water bodies from polygons before generating dots to avoid dots in rivers/lakes
 * Supports both single-variable and multi-variable dot density maps
 *
 * @param {Array<Graphic>} enrichedGraphics - Graphics with polygon geometries and attribute values
 * @param {Array<Object>} attributeConfigs - Array of attribute configurations, each with:
 *   - field: Field name containing the value
 *   - color: Color for dots of this variable
 *   - label: Optional label for legend
 *   - dotValue: Optional per-attribute dot value (overrides default)
 * @param {number} dotValue - Default number of units each dot represents
 * @param {number} dotSize - Size of each dot in pixels
 * @param {string} title - Layer title
 * @param {Object} spatialReference - Spatial reference for the layer
 * @returns {Promise<GraphicsLayer>} GraphicsLayer with point graphics
 */
async function createCustomDotDensityLayer(enrichedGraphics, attributeConfigs, dotValue, dotSize, title, spatialReference, options = {}) {
  const startTime = performance.now();
  const { skipWaterClipping = false } = options;

  console.log(`[createCustomDotDensityLayer] Creating optimized dot layer for ${enrichedGraphics.length} polygons`);

  // OPTIMIZATION: Skip water clipping if requested (it's expensive)
  let workingGraphics = enrichedGraphics;
  if (!skipWaterClipping) {
    workingGraphics = await clipWaterBodiesFromGraphics(enrichedGraphics);
  }

  // OPTIMIZATION: Pre-buffer all polygons ONCE before generating any dots
  const bufferedPolygons = workingGraphics.map(graphic => {
    if (!graphic.geometry) return null;
    try {
      const buffered = geometryEngine.buffer(graphic.geometry, -100, "meters");
      return (buffered && buffered.rings && buffered.rings.length > 0) ? buffered : graphic.geometry;
    } catch {
      return graphic.geometry;
    }
  });

  // OPTIMIZATION: Pre-create symbols for each color (reuse instead of creating per-dot)
  const symbolCache = {};
  attributeConfigs.forEach(attrConfig => {
    const color = attrConfig.color || '#E60049';
    if (!symbolCache[color]) {
      symbolCache[color] = new SimpleMarkerSymbol({
        style: "circle",
        size: dotSize,
        color: color,
        outline: null
      });
    }
  });

  const allDotGraphics = [];
  let totalDotsGenerated = 0;
  const baseSeed = 1;

  // STEP 2: Generate dots for EACH attribute/variable
  attributeConfigs.forEach((attrConfig, attrIndex) => {
    const field = attrConfig.field;
    const attrDotValue = attrConfig.dotValue || dotValue;
    const color = attrConfig.color || '#E60049';
    const symbol = symbolCache[color]; // Reuse pre-created symbol

    // OPTIMIZATION: Process all polygons for this attribute
    workingGraphics.forEach((graphic, polygonIndex) => {
      const originalGraphic = enrichedGraphics[polygonIndex];
      const value = originalGraphic?.attributes[field] || graphic.attributes[field] || 0;
      const numDots = Math.max(0, Math.floor(value / attrDotValue));

      if (numDots > 0 && graphic.geometry) {
        const seed = baseSeed + (attrIndex * 1000) + polygonIndex;
        const preBuffered = bufferedPolygons[polygonIndex];
        const polygonName = graphic.attributes.NAME || graphic.attributes.ID || `Area ${polygonIndex}`;

        // OPTIMIZATION: Pass pre-buffered polygon to avoid re-computing buffer
        const points = generatePointsInPolygon(graphic.geometry, numDots, seed, polygonName, preBuffered);

        // OPTIMIZATION: Batch create graphics with shared symbol reference
        for (let i = 0; i < points.length; i++) {
          allDotGraphics.push(new Graphic({
            geometry: points[i],
            symbol: symbol, // Reuse symbol object
            attributes: {
              SOURCE_NAME: polygonName,
              SOURCE_VALUE: value,
              FIELD: field,
              VARIABLE_INDEX: attrIndex
            }
          }));
        }

        totalDotsGenerated += points.length;
      }
    });
  });

  const elapsed = (performance.now() - startTime).toFixed(0);
  console.log(`[createCustomDotDensityLayer] Generated ${totalDotsGenerated} dots in ${elapsed}ms`);

  const dotLayer = new GraphicsLayer({
    title: title || (attributeConfigs.length > 1 ? 'Multi-Variable Dot Density' : `${attributeConfigs[0]?.field} Dot Density`),
    graphics: allDotGraphics
  });

  dotLayer.isVisualizationLayer = true;
  dotLayer.visualizationType = 'custom-dotdensity';
  dotLayer.isDotDensity = true;
  dotLayer.isCustomDotDensity = true;

  dotLayer.legendInfo = {
    field: attributeConfigs[0]?.field,
    title: title || (attributeConfigs.length > 1 ? 'Dot Density' : attributeConfigs[0]?.field),
    isDotDensity: true,
    dotValue: dotValue,
    dotSize: dotSize,
    color: attributeConfigs[0]?.color,
    isCustom: true,
    isMultiVariable: attributeConfigs.length > 1,
    attributes: attributeConfigs.map(a => ({
      field: a.field,
      color: a.color,
      label: a.label || a.field
    }))
  };

  return dotLayer;
}

/**
 * Helper: Build a dot density layer from enriched graphics
 * Used by both fresh enrichment and cached data paths
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.useCustomDotPlacement - If true, uses custom point-in-polygon dot generation
 *   instead of Esri's DotDensityRenderer. This guarantees dots are placed only within actual polygon
 *   boundaries and respects holes (rivers, lakes, etc.). Default: true for accurate placement.
 * @returns {Promise<FeatureLayer|GraphicsLayer>} The dot density layer
 */
async function buildDotDensityLayerFromGraphics(
  enrichedGraphics,
  fieldsToEnrich,
  hasMultipleAttributes,
  attributes,
  configDotValue,
  dotSize,
  color,
  title,
  areaType,
  queryField,
  options = {}
) {
  const { useCustomDotPlacement = true } = options; // Default to custom placement for accuracy
  const primaryField = fieldsToEnrich[0];

  // Calculate statistics and auto-determine dotValue if not provided
  const values = enrichedGraphics.map(g => g.attributes[primaryField] || 0).filter(v => v > 0);
  const totalValue = values.reduce((a, b) => a + b, 0);
  const maxValue = Math.max(...values);
  const avgValue = values.length > 0 ? totalValue / values.length : 0;

  // Get default dotValue based on area type
  const getDefaultDotValueForAreaType = (areaTypeStr) => {
    const areaTypeLower = (areaTypeStr || '').toLowerCase();
    switch (areaTypeLower) {
      case 'state': return 5000;
      case 'county': return 500;
      case 'cbsa': return 1000;
      case 'place': return 100;
      case 'zip': return 100;
      case 'tract': return 10;
      case 'block_group': return 5;
      default: return 100;
    }
  };

  const roundToNiceNumber = (value) => {
    if (value <= 0) return 1;
    if (value <= 50) return Math.round(value / 10) * 10 || 10;
    if (value <= 100) return Math.round(value / 25) * 25;
    if (value <= 500) return Math.round(value / 50) * 50;
    if (value <= 1000) return Math.round(value / 100) * 100;
    if (value <= 5000) return Math.round(value / 500) * 500;
    if (value <= 10000) return Math.round(value / 1000) * 1000;
    if (value <= 50000) return Math.round(value / 5000) * 5000;
    return Math.round(value / 10000) * 10000;
  };

  let dotValue = configDotValue ?? attributes?.[0]?.value;
  if (!dotValue || dotValue === 1) {
    // Use area type default directly - don't override with calculated values
    dotValue = getDefaultDotValueForAreaType(areaType);
  }

  console.log(`[buildDotDensityLayer] Stats: total=${totalValue}, max=${maxValue}, avg=${avgValue.toFixed(0)}, dotValue=${dotValue}`);

  // Use custom dot placement for accurate boundary-respecting placement
  if (useCustomDotPlacement) {
    console.log(`[buildDotDensityLayer] Using CUSTOM dot placement for accurate boundary placement`);

    // Build attribute configs for all variables (single or multi-variable)
    const attributeConfigs = hasMultipleAttributes
      ? attributes.map(attr => ({
          field: attr.field,
          color: attr.color || '#E60049',
          label: attr.label || attr.field,
          dotValue: attr.value || dotValue
        }))
      : [{
          field: primaryField,
          color: color,
          label: attributes?.[0]?.label || title || primaryField,
          dotValue: dotValue
        }];

    console.log(`[buildDotDensityLayer] ${hasMultipleAttributes ? 'Multi-variable' : 'Single-variable'} mode with ${attributeConfigs.length} attribute(s)`);

    const spatialRef = enrichedGraphics[0]?.geometry?.spatialReference || { wkid: 102100 };
    const customLayer = await createCustomDotDensityLayer(
      enrichedGraphics,
      attributeConfigs,
      dotValue,
      dotSize,
      title || (hasMultipleAttributes ? 'Multi-Variable Dot Density' : `${primaryField} Dot Density`),
      spatialRef
    );

    // Store serializable enriched features for caching
    customLayer.enrichedFeatures = enrichedGraphics.map(g => ({
      geometry: {
        rings: g.geometry.rings,
        spatialReference: g.geometry.spatialReference?.toJSON?.() || g.geometry.spatialReference || { wkid: 102100 }
      },
      attributes: { ...g.attributes }
    }));

    console.log(`[buildDotDensityLayer] Custom layer created with accurate boundary placement`);
    return customLayer;
  }

  // Original Esri DotDensityRenderer approach (may place dots outside polygon boundaries)
  console.log(`[buildDotDensityLayer] Using Esri DotDensityRenderer (may have boundary issues)`);

  const rendererAttributes = hasMultipleAttributes
    ? attributes.map(attr => ({
        field: attr.field,
        color: attr.color || '#E60049',
        label: attr.label || attr.field
      }))
    : [{
        field: primaryField,
        color: color,
        label: attributes?.[0]?.label || title || primaryField
      }];

  const dotDensityRenderer = new DotDensityRenderer({
    dotValue: dotValue,
    dotSize: dotSize,
    attributes: rendererAttributes,
    outline: null,
    seed: 1,
    referenceScale: 50000,
    dotBlendingEnabled: false,  // Disable blending to ensure perfect circles
    legendOptions: {
      unit: hasMultipleAttributes ? "people" : (attributes?.[0]?.label || title || primaryField)
    }
  });

  const layerFields = [
    { name: "OBJECTID", type: "oid" },
    { name: "NAME", type: "string" },
    { name: queryField, type: "string" },
    ...fieldsToEnrich.map(f => ({ name: f, type: "double" }))
  ];

  const popupFieldInfos = fieldsToEnrich.map(f => {
    const attrConfig = hasMultipleAttributes ? attributes.find(a => a.field === f) : null;
    return {
      fieldName: f,
      label: attrConfig?.label || f,
      format: { digitSeparator: true, places: 0 }
    };
  });

  const dotDensityLayer = new FeatureLayer({
    source: enrichedGraphics,
    objectIdField: "OBJECTID",
    geometryType: "polygon",
    spatialReference: enrichedGraphics[0]?.geometry?.spatialReference || { wkid: 102100 },
    fields: layerFields,
    renderer: dotDensityRenderer,
    title: title || (hasMultipleAttributes ? 'Multi-Variable Dot Density' : `${primaryField} Dot Density`),
    popupTemplate: new PopupTemplate({
      title: "{NAME}",
      content: [{
        type: "fields",
        fieldInfos: popupFieldInfos
      }]
    })
  });

  dotDensityLayer.isVisualizationLayer = true;
  dotDensityLayer.visualizationType = 'geography-dotdensity';
  dotDensityLayer.isDotDensity = true;

  dotDensityLayer.legendInfo = {
    field: primaryField,
    title: title || (hasMultipleAttributes ? 'Dot Density' : primaryField),
    isDotDensity: true,
    dotValue: dotValue,
    dotSize: dotSize,
    color: color,
    attributes: rendererAttributes,
    isMultiVariable: hasMultipleAttributes
  };

  // Store serializable enriched features for caching (geometry + attributes only)
  dotDensityLayer.enrichedFeatures = enrichedGraphics.map(g => ({
    geometry: {
      rings: g.geometry.rings,
      spatialReference: g.geometry.spatialReference?.toJSON?.() || g.geometry.spatialReference || { wkid: 102100 }
    },
    attributes: { ...g.attributes }
  }));

  console.log(`[buildDotDensityLayer] Layer created with ${enrichedGraphics.length} features, cached for future loads`);
  return dotDensityLayer;
}

/**
 * Creates a TRUE dot density layer using Esri's DotDensityRenderer
 * Queries features, enriches them, creates a client-side FeatureLayer with DotDensityRenderer
 *
 * @param {Object} config - Configuration object containing:
 *   - field: Variable field to visualize (e.g., 'TOTPOP_CY')
 *   - areaType: Type of geography (e.g., "county", "tract")
 *   - dotValue: Number of units each dot represents (e.g., 10000 = 1 dot per 10000 people)
 *   - dotSize: Size of each dot in pixels
 *   - color: Color for the dots
 *   - viewExtent: Map extent for filtering
 *   - enrichedFeatures: (optional) Pre-enriched data from saved config - skips enrichment if present
 * @param {Object} selectedAreaType - Area type configuration with URL
 * @returns {Promise<FeatureLayer>} FeatureLayer with DotDensityRenderer
 */
async function createGeographyDotDensityLayer(config, selectedAreaType) {
  console.log("[createGeographyDotDensityLayer] Creating TRUE dot density layer with DotDensityRenderer, config:", config);

  try {
    const {
      field,
      areaType,
      title,
      viewExtent,
      geographyList,
      dotValue: configDotValue,
      dotSize: configDotSize,
      color: configColor,
      attributes,
      renderMode,
      enrichedFeatures  // Pre-enriched data from saved config - skip enrichment if present
    } = config;

    // Determine if we have multiple attributes (multi-variable dot density)
    const hasMultipleAttributes = attributes && Array.isArray(attributes) && attributes.length > 0;
    const isMultiVariable = attributes && Array.isArray(attributes) && attributes.length > 1;

    // Get dotSize from config or attributes - default 5 for all dot density maps
    const defaultDotSize = 5;
    const dotSize = configDotSize ?? attributes?.[0]?.dotSize ?? defaultDotSize;

    // Build the list of fields to enrich
    const fieldsToEnrich = hasMultipleAttributes
      ? attributes.map(attr => attr.field).filter(Boolean)
      : field ? [field] : [];

    // Get color from config or attributes[0].color (from DotDensityEditor)
    // DotDensityEditor stores hex strings like '#E60049', which Color class accepts
    const color = configColor || attributes?.[0]?.color || '#E60049';

    if (!selectedAreaType?.url) {
      console.error("[createGeographyDotDensityLayer] No area type URL provided");
      return null;
    }

    if (fieldsToEnrich.length === 0) {
      console.error("[createGeographyDotDensityLayer] No field(s) specified for dot density");
      return null;
    }

    console.log(`[createGeographyDotDensityLayer] Will enrich ${fieldsToEnrich.length} field(s):`, fieldsToEnrich);

    const isExtentBased = renderMode === 'extent-based-geography-dotdensity';

    // Determine query field based on area type
    const queryField = areaType === 'county' ? 'ID' :
                      areaType === 'zip' ? 'ID' :
                      areaType === 'tract' ? 'GEOID' : 'ID';

    // ========================================
    // FAST PATH: Use cached enriched data if available
    // This skips the expensive query + enrichment process
    // ========================================
    if (enrichedFeatures && Array.isArray(enrichedFeatures) && enrichedFeatures.length > 0) {
      console.log(`[createGeographyDotDensityLayer] USING CACHED DATA - ${enrichedFeatures.length} pre-enriched features`);

      // Reconstruct graphics from cached data
      const enrichedGraphics = enrichedFeatures.map((featureData, index) => {
        return new Graphic({
          geometry: new Polygon({
            rings: featureData.geometry.rings,
            spatialReference: featureData.geometry.spatialReference || { wkid: 102100 }
          }),
          attributes: {
            ...featureData.attributes,
            OBJECTID: featureData.attributes.OBJECTID || index + 1
          }
        });
      });

      // Build the layer from cached data (skip to renderer creation)
      return await buildDotDensityLayerFromGraphics(
        enrichedGraphics,
        fieldsToEnrich,
        hasMultipleAttributes,
        attributes,
        configDotValue,
        dotSize,
        color,
        title,
        areaType,
        queryField
      );
    }

    // Create a temporary FeatureLayer to query features
    const tempLayer = new FeatureLayer({
      url: selectedAreaType.url
    });

    await tempLayer.load();
    console.log("[createGeographyDotDensityLayer] Loaded feature layer");

    // Check if the field already exists in the feature service
    const fieldExistsInService = tempLayer.fields.some(f => f.name === field);
    console.log(`[createGeographyDotDensityLayer] Field ${field} exists in service: ${fieldExistsInService}`);

    // Build the query
    const queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];

    if (isExtentBased && viewExtent) {
      queryObj.geometry = new Extent({
        xmin: viewExtent.xmin,
        ymin: viewExtent.ymin,
        xmax: viewExtent.xmax,
        ymax: viewExtent.ymax,
        spatialReference: viewExtent.spatialReference || { wkid: 102100 }
      });
      queryObj.spatialRelationship = "intersects";
      queryObj.where = "1=1";
      console.log("[createGeographyDotDensityLayer] Querying by extent");
    } else if (geographyList && geographyList.length > 0) {
      const quotedIds = geographyList.map(id => `'${id}'`).join(',');
      queryObj.where = `${queryField} IN (${quotedIds})`;
      console.log("[createGeographyDotDensityLayer] Querying by ID list");
    } else {
      console.error("[createGeographyDotDensityLayer] No extent or geography list provided");
      return null;
    }

    // Execute the query
    const queryResult = await tempLayer.queryFeatures(queryObj);

    if (!queryResult.features || queryResult.features.length === 0) {
      console.warn("[createGeographyDotDensityLayer] No features found");
      return null;
    }

    console.log(`[createGeographyDotDensityLayer] Found ${queryResult.features.length} features`);

    // Prepare graphics with enriched data
    let enrichedGraphics = [];

    // Check which fields exist in the service
    const fieldsInService = fieldsToEnrich.filter(f =>
      tempLayer.fields.some(layerField => layerField.name === f)
    );
    const fieldsNeedingEnrichment = fieldsToEnrich.filter(f =>
      !tempLayer.fields.some(layerField => layerField.name === f)
    );

    console.log(`[createGeographyDotDensityLayer] Fields in service: ${fieldsInService.join(', ') || 'none'}`);
    console.log(`[createGeographyDotDensityLayer] Fields needing enrichment: ${fieldsNeedingEnrichment.join(', ') || 'none'}`);

    if (fieldsNeedingEnrichment.length === 0) {
      // All fields exist in the service - just copy features as graphics
      enrichedGraphics = queryResult.features.map(f => {
        return new Graphic({
          geometry: f.geometry,
          attributes: {
            ...f.attributes,
            OBJECTID: f.attributes.OBJECTID || f.attributes.FID || Math.random() * 100000
          }
        });
      });
      console.log("[createGeographyDotDensityLayer] Using field values from feature service");
    } else {
      // Need to enrich for the fields not in service
      console.log("[createGeographyDotDensityLayer] Fields not in service, enriching...");

      const { enrichmentService } = await import('../../services/enrichmentService');
      const projection = await import('@arcgis/core/geometry/projection');
      await projection.load();

      // Convert features to market area format for enrichment
      const marketAreas = queryResult.features.map((feature, index) => {
        const identifier = feature.attributes[queryField] ||
                          feature.attributes.NAME ||
                          `Area ${index}`;

        const originalPolygon = new Polygon({
          rings: feature.geometry.rings,
          spatialReference: feature.geometry.spatialReference
        });

        const projectedPolygon = projection.project(originalPolygon, { wkid: 3857 });

        return {
          id: `geo-${index}`,
          name: identifier,
          ma_type: areaType,
          type: areaType,
          locations: [{
            name: identifier,
            geometry: {
              rings: projectedPolygon.rings,
              spatialReference: projectedPolygon.spatialReference
            }
          }],
          attributes: feature.attributes
        };
      });

      // Enrich the areas with ALL fields that need enrichment
      const enrichmentResult = await enrichmentService.enrichAreas(
        marketAreas,
        fieldsNeedingEnrichment,
        false,
        '2025'
      );

      if (!enrichmentResult || !enrichmentResult.results) {
        console.error("[createGeographyDotDensityLayer] Enrichment failed");
        return null;
      }

      console.log(`[createGeographyDotDensityLayer] Enrichment complete: ${enrichmentResult.results.length} results for ${fieldsNeedingEnrichment.length} fields`);

      // Create graphics with all enriched values added to attributes
      enrichedGraphics = queryResult.features.map((feature, index) => {
        const enrichResult = enrichmentResult.results[index];
        const enrichedAttrs = enrichResult?.value?.FeatureSet?.[0]?.features?.[0]?.attributes || {};

        // Build attributes with all enriched fields
        const newAttributes = {
          ...feature.attributes,
          OBJECTID: feature.attributes.OBJECTID || feature.attributes.FID || index + 1
        };

        // Add each enriched field
        fieldsNeedingEnrichment.forEach(fieldName => {
          newAttributes[fieldName] = enrichedAttrs[fieldName] || 0;
        });

        return new Graphic({
          geometry: feature.geometry,
          attributes: newAttributes
        });
      });
    }

    // Log sample values for all fields
    const primaryField = fieldsToEnrich[0];
    enrichedGraphics.slice(0, 3).forEach((g, i) => {
      const fieldValues = fieldsToEnrich.map(f => `${f}=${g.attributes[f]}`).join(', ');
      console.log(`[createGeographyDotDensityLayer] Feature ${i} (${g.attributes.NAME}): ${fieldValues}`);
    });

    // Use helper function to build the layer (same as cached path)
    console.log(`[createGeographyDotDensityLayer] Building layer from ${enrichedGraphics.length} freshly enriched graphics`);
    return await buildDotDensityLayerFromGraphics(
      enrichedGraphics,
      fieldsToEnrich,
      hasMultipleAttributes,
      attributes,
      configDotValue,
      dotSize,
      color,
      title,
      areaType,
      queryField
    );

  } catch (error) {
    console.error("[createGeographyDotDensityLayer] Error creating dot density layer:", error);
    return null;
  }
}

/**
 * Helper: Build a heat map layer from cached enriched features
 * Used to avoid re-enrichment when loading a saved map
 */
function buildHeatMapLayerFromCachedData(cachedFeatures, config, field, title, areaType, decimalPlaces = 0, useCommas = true, numberFormat = 'number') {
  console.log(`[buildHeatMapLayerFromCachedData] Rebuilding layer from ${cachedFeatures.length} cached features`);

  // Extract classBreakInfos from config if available
  let classBreakInfos = config.classBreakInfos;
  if (classBreakInfos && !Array.isArray(classBreakInfos)) {
    classBreakInfos = classBreakInfos.classBreakInfos || [];
  }

  // Rebuild symbols from classBreakInfos
  const heatmapColors = [
    [128, 0, 128, 0.35], [0, 0, 139, 0.20], [135, 206, 235, 0.35],
    [144, 238, 144, 0.35], [255, 255, 144, 0.35], [255, 165, 0, 0.15], [255, 99, 71, 0.15]
  ];

  // Rebuild classBreakInfos with proper symbol instances if they don't have them
  if (classBreakInfos && classBreakInfos.length > 0) {
    classBreakInfos = classBreakInfos.map((breakInfo, index) => {
      let symbol = breakInfo.symbol;
      if (!(symbol instanceof SimpleFillSymbol)) {
        const colorArray = Array.isArray(breakInfo.symbol?.color)
          ? breakInfo.symbol.color
          : heatmapColors[index % heatmapColors.length];
        symbol = new SimpleFillSymbol({
          color: new Color(colorArray),
          outline: new SimpleLineSymbol({
            color: new Color([255, 255, 255, 0.3]),
            width: 0.5
          })
        });
      }
      return { ...breakInfo, symbol };
    });
  }

  // Helper to get symbol for a value
  const getSymbolForValue = (value) => {
    if (!classBreakInfos || classBreakInfos.length === 0) {
      return new SimpleFillSymbol({
        color: new Color([128, 128, 128, 0.2]),
        outline: new SimpleLineSymbol({ color: new Color([255, 255, 255, 0.3]), width: 0.5 })
      });
    }
    for (const breakInfo of classBreakInfos) {
      if (value >= breakInfo.minValue && value <= breakInfo.maxValue) {
        return breakInfo.symbol;
      }
    }
    return classBreakInfos[classBreakInfos.length - 1].symbol;
  };

  // Rebuild graphics from cached data
  const graphics = cachedFeatures.map((cached, index) => {
    const value = cached.attributes[field] || 0;
    const symbol = getSymbolForValue(value);

    let geometry;
    if (cached.geometry.type === 'point' || cached.geometry.x !== null) {
      geometry = new Point({
        x: cached.geometry.x,
        y: cached.geometry.y,
        spatialReference: cached.geometry.spatialReference
      });
    } else {
      geometry = new Polygon({
        rings: cached.geometry.rings,
        spatialReference: cached.geometry.spatialReference
      });
    }

    return new Graphic({
      geometry: geometry,
      attributes: cached.attributes,
      symbol: symbol
    });
  });

  const graphicsLayer = new GraphicsLayer({
    graphics: graphics,
    title: title || `Geography Heat Map - ${field}`,
    popupTemplate: new PopupTemplate({
      title: "{NAME}",
      content: [{
        type: "fields",
        fieldInfos: [{ fieldName: field, label: field, format: { digitSeparator: true, places: 2 } }]
      }]
    })
  });

  graphicsLayer.isVisualizationLayer = true;
  graphicsLayer.visualizationType = 'geography-heatmap';
  graphicsLayer.isDotDensity = false;

  graphicsLayer.legendInfo = {
    field: field,
    classBreakInfos: classBreakInfos,
    title: title || `Geography Heat Map - ${field}`,
    isDotDensity: false
  };

  // Re-store the cached features for future loads
  graphicsLayer.enrichedFeatures = cachedFeatures;

  console.log(`[buildHeatMapLayerFromCachedData] Layer rebuilt with ${graphics.length} graphics from cache`);
  return graphicsLayer;
}

/**
 * Creates a geography-specific heat map layer by geocoding and enriching specific areas
 * Only renders the specified geographic areas from the user's upload
 *
 * @param {Object} config - Configuration object containing:
 *   - geographyList: Array of area identifiers (e.g., ["90210", "90211"] for zip codes)
 *   - areaType: Type of geography (e.g., "zip", "county", "place", "tract")
 *   - field: Variable to enrich with
 *   - visualizationKey: Key for visualization
 *   - enrichedFeatures: (optional) Pre-enriched data from saved config - skips enrichment if present
 * @param {Object} selectedAreaType - Area type configuration with URL
 * @returns {Promise<GraphicsLayer>} Graphics layer with enriched geography polygons
 */
async function createGeographyHeatMapLayer(config, selectedAreaType) {
  console.log("[createGeographyHeatMapLayer] Creating geography heat map with config:", config);

  try {
    const { geographyList, areaType, field, visualizationKey, title, renderMode, viewExtent, visualizationType, decimalPlaces = 0, useCommas = true, numberFormat = 'number', enrichedFeatures: cachedEnrichedFeatures } = config;

    console.log("[createGeographyHeatMapLayer] DEBUG - Checking inputs:", {
      hasGeographyList: !!geographyList,
      isArray: Array.isArray(geographyList),
      length: geographyList?.length,
      areaType,
      field,
      renderMode,
      visualizationType,
      hasViewExtent: !!viewExtent,
      hasSelectedAreaType: !!selectedAreaType,
      selectedAreaTypeUrl: selectedAreaType?.url
    });

    // Validate based on render mode
    const isExtentBased = renderMode === 'extent-based-geography' || renderMode === 'extent-based-geography-dotdensity';
    const isDotDensity = visualizationType === 'dotdensity' || renderMode?.includes('dotdensity');

    console.log("[createGeographyHeatMapLayer] Rendering mode:", { isExtentBased, isDotDensity, renderMode, visualizationType });

    // Route to TRUE dot density layer if this is a dot density map
    if (isDotDensity) {
      console.log("[createGeographyHeatMapLayer] Routing to createGeographyDotDensityLayer for TRUE dot density rendering");
      return await createGeographyDotDensityLayer(config, selectedAreaType);
    }

    // ========================================
    // FAST PATH: Use cached enriched data if available
    // This skips the expensive query + enrichment process
    // ========================================
    if (cachedEnrichedFeatures && Array.isArray(cachedEnrichedFeatures) && cachedEnrichedFeatures.length > 0) {
      console.log(`[createGeographyHeatMapLayer] USING CACHED DATA - ${cachedEnrichedFeatures.length} pre-enriched features`);
      return buildHeatMapLayerFromCachedData(cachedEnrichedFeatures, config, field, title, areaType, decimalPlaces, useCommas, numberFormat);
    }

    if (!isExtentBased && (!geographyList || !Array.isArray(geographyList) || geographyList.length === 0)) {
      console.error("[createGeographyHeatMapLayer] EARLY RETURN 1: No geography list provided for selective rendering");
      return null;
    }

    if (isExtentBased && !viewExtent) {
      console.error("[createGeographyHeatMapLayer] EARLY RETURN 1b: No extent provided for extent-based rendering");
      return null;
    }

    // FIXED: Use config's areaType to determine the correct feature layer URL
    // This ensures saved maps load with the correct area type (e.g., county vs tract)
    let effectiveAreaType = selectedAreaType;
    if (areaType && FEATURE_LAYERS[areaType]) {
      // Config has a saved areaType - use that instead of the passed-in selectedAreaType
      if (!selectedAreaType?.url || selectedAreaType.url !== FEATURE_LAYERS[areaType].url) {
        console.log(`[createGeographyHeatMapLayer] Using config areaType '${areaType}' instead of selectedAreaType`);
        effectiveAreaType = FEATURE_LAYERS[areaType];
      }
    }

    if (!effectiveAreaType?.url) {
      console.error("[createGeographyHeatMapLayer] EARLY RETURN 2: No area type URL provided");
      return null;
    }

    // Log appropriate message based on render mode
    if (isExtentBased) {
      console.log(`[createGeographyHeatMapLayer] Querying ${areaType} areas within map extent`);
    } else {
      console.log(`[createGeographyHeatMapLayer] Geocoding ${geographyList.length} ${areaType} areas`);
    }

    // Import enrichment service
    const { enrichmentService } = await import('../../services/enrichmentService');

    // Create a temporary FeatureLayer to query field information
    const tempLayer = new FeatureLayer({
      url: effectiveAreaType.url
    });

    // Load the layer to access its fields
    await tempLayer.load();
    console.log(`[createGeographyHeatMapLayer] Loaded feature layer, available fields:`,
      tempLayer.fields.map(f => f.name).join(', '));

    // Map area type to potential field names (in priority order)
    // We'll try to find which one actually exists in the feature service
    const areaFieldCandidates = {
      'zip': ['ID', 'ZIP_CODE', 'ZIPCODE', 'ZIP', 'ZCTA5', 'ZCTA', 'GEOID', 'NAME'],
      'county': ['ID', 'NAME', 'COUNTY_NAME', 'COUNTYNAME', 'GEOID'],
      'place': ['ID', 'NAME', 'PLACE_NAME', 'PLACENAME', 'GEOID'],
      'tract': ['ID', 'GEOID', 'TRACT', 'NAME'],
      'block_group': ['ID', 'GEOID', 'BLKGRP', 'NAME'],
      'cbsa': ['ID', 'NAME', 'CBSA_NAME', 'CBSANAME', 'GEOID'],
      'state': ['ID', 'NAME', 'STATE_NAME', 'STATENAME', 'STUSPS', 'GEOID']
    };

    // Find the correct field by checking which candidate exists in the layer
    const candidates = areaFieldCandidates[areaType] || ['NAME'];
    const layerFieldNames = tempLayer.fields.map(f => f.name.toUpperCase());

    let queryField = null;
    for (const candidate of candidates) {
      if (layerFieldNames.includes(candidate.toUpperCase())) {
        // Get the actual field name with correct casing
        queryField = tempLayer.fields.find(f => f.name.toUpperCase() === candidate.toUpperCase()).name;
        break;
      }
    }

    if (!queryField) {
      console.error(`[createGeographyHeatMapLayer] EARLY RETURN 3: Could not find suitable field for ${areaType}. Available fields:`, layerFieldNames);
      return null;
    }

    console.log(`[createGeographyHeatMapLayer] Using field ${queryField} for querying ${areaType} areas`);

    // First, do a sample query to verify field content
    const sampleQuery = new Query({
      where: '1=1',
      outFields: [queryField, 'NAME'],
      returnGeometry: false,
      num: 5
    });

    const sampleResult = await tempLayer.queryFeatures(sampleQuery);
    if (sampleResult.features && sampleResult.features.length > 0) {
      console.log(`[createGeographyHeatMapLayer] Sample ${queryField} values from feature service:`,
        sampleResult.features.map(f => ({
          [queryField]: f.attributes[queryField],
          NAME: f.attributes.NAME
        }))
      );
    }

    // Build query based on render mode
    let queryObj;
    if (isExtentBased) {
      // For extent-based rendering, query all features within the extent
      console.log("[createGeographyHeatMapLayer] Querying by extent:", viewExtent);

      // Create a proper Extent object for the query
      const extentGeometry = new Extent({
        xmin: viewExtent.xmin,
        ymin: viewExtent.ymin,
        xmax: viewExtent.xmax,
        ymax: viewExtent.ymax,
        spatialReference: { wkid: viewExtent.spatialReference }
      });

      queryObj = new Query({
        where: '1=1', // Get all features within the geometry
        geometry: extentGeometry,
        spatialRelationship: 'intersects',
        outFields: ['*'],
        returnGeometry: true,
        outSpatialReference: { wkid: 4326 }
      });
    } else {
      // For selective rendering, query specific areas by ID
      console.log("[createGeographyHeatMapLayer] Geography list being queried:", geographyList.map(area => ({
        value: area,
        type: typeof area,
        length: area?.length
      })));

      const whereClause = `${queryField} IN (${geographyList.map(area => `'${area}'`).join(', ')})`;

      queryObj = new Query({
        where: whereClause,
        outFields: ['*'],
        returnGeometry: true,
        outSpatialReference: { wkid: 4326 }
      });
    }

    console.log("[createGeographyHeatMapLayer] Querying feature service:", {
      url: effectiveAreaType.url,
      where: queryObj.where,
      field: queryField,
      areaType: areaType
    });

    const queryResult = await tempLayer.queryFeatures(queryObj);

    if (!queryResult.features || queryResult.features.length === 0) {
      console.warn("[createGeographyHeatMapLayer] No features found for the specified areas");
      return null;
    }

    console.log(`[createGeographyHeatMapLayer] Found ${queryResult.features.length} features`);

    // Check if classBreakInfos already exist in config (this means data is already enriched)
    let breaksArray = null;
    if (config.classBreakInfos) {
      if (Array.isArray(config.classBreakInfos)) {
        breaksArray = config.classBreakInfos;
      } else if (config.classBreakInfos.classBreakInfos && Array.isArray(config.classBreakInfos.classBreakInfos)) {
        breaksArray = config.classBreakInfos.classBreakInfos;
      }
    }

    const hasExistingBreaks = breaksArray && breaksArray.length > 0;

    // Check if the field exists in the feature attributes (demographic data already present)
    const firstFeature = queryResult.features[0];
    const hasFieldInFeatures = firstFeature.attributes && (field in firstFeature.attributes);

    console.log(`[createGeographyHeatMapLayer] Field ${field} ${hasFieldInFeatures ? 'found' : 'not found'} in feature attributes, hasExistingBreaks: ${hasExistingBreaks}`);

    let enrichedValues;
    let enrichedFeatures;

    // FIXED: Even with existing classBreakInfos, we need actual data values to render correctly
    // Only skip enrichment if we have both existing breaks AND the field is in the features
    if (hasFieldInFeatures) {
      // Use data directly from the feature service (already enriched)
      console.log("[createGeographyHeatMapLayer] Using demographic data from feature service");

      enrichedValues = queryResult.features.map((feature, idx) => {
        const value = feature.attributes[field];
        console.log(`[createGeographyHeatMapLayer] Feature ${idx} (${feature.attributes[queryField]}) ${field} value:`, value);
        return value !== undefined && value !== null ? parseFloat(value) : 0;
      }).filter(val => !isNaN(val));

      enrichedFeatures = queryResult.features;

    } else {
      // Fall back to enrichment service if field not present
      console.log("[createGeographyHeatMapLayer] Field not in features, calling enrichment service");

      // Import projection utility
      const projection = await import('@arcgis/core/geometry/projection');
      await projection.load();

      // Convert features to market area format for enrichment with proper projection
      const marketAreas = queryResult.features.map((feature, index) => {
        const identifier = feature.attributes[queryField] ||
                          feature.attributes.NAME ||
                          feature.attributes.GEOID ||
                          `Area ${index}`;

        // Create polygon in the original spatial reference
        const originalPolygon = new Polygon({
          rings: feature.geometry.rings,
          spatialReference: feature.geometry.spatialReference
        });

        // Project to Web Mercator (WKID 3857) which is what the enrichment service expects
        const projectedPolygon = projection.project(originalPolygon, { wkid: 3857 });

        console.log(`[createGeographyHeatMapLayer] Projected ${identifier} from ${feature.geometry.spatialReference?.wkid} to 3857`);

        return {
          id: `geo-${index}`,
          name: identifier,
          ma_type: areaType,
          type: areaType,
          locations: [{
            name: identifier,
            geometry: {
              rings: projectedPolygon.rings,
              spatialReference: projectedPolygon.spatialReference
            }
          }],
          attributes: feature.attributes
        };
      });

      // Enrich the areas with the selected variable
      const enrichmentResult = await enrichmentService.enrichAreas(
        marketAreas,
        [field],
        false,
        '2025'
      );

      if (!enrichmentResult || !enrichmentResult.results || enrichmentResult.results.length === 0) {
        console.error("[createGeographyHeatMapLayer] Enrichment failed or returned no data");
        return null;
      }

      console.log("[createGeographyHeatMapLayer] Enrichment completed, results count:", enrichmentResult.results.length);

      enrichedValues = enrichmentResult.results.map((result, idx) => {
        const value = result.value?.FeatureSet?.[0]?.features?.[0]?.attributes?.[field];
        console.log(`[createGeographyHeatMapLayer] Result ${idx} ${field} value:`, value);
        return value !== undefined && value !== null ? parseFloat(value) : 0;
      }).filter(val => !isNaN(val));

      enrichedFeatures = enrichmentResult.results;
    }

    if (enrichedValues.length === 0) {
      console.error("[createGeographyHeatMapLayer] No valid enriched values found");
      return null;
    }

    console.log("[createGeographyHeatMapLayer] Enriched values:", enrichedValues);

    let classBreakInfos;

    // Use existing classBreakInfos if available (already extracted earlier)
    if (hasExistingBreaks) {
      console.log("[createGeographyHeatMapLayer] Using existing classBreakInfos from config:", breaksArray.length, "breaks, isDotDensity:", isDotDensity);

      // Use existing classBreakInfos but ensure symbols are properly formatted
      classBreakInfos = breaksArray.map((breakInfo, index) => {
        let symbol = breakInfo.symbol;
        let colorArray = Array.isArray(breakInfo.symbol?.color)
          ? [...breakInfo.symbol.color]  // Clone to avoid mutating original
          : [128, 0, 128, 0.35];

        if (isDotDensity) {
          // For dot density, boost opacity since small dots need to be visible
          // Original colors are designed for polygon fills with low opacity
          if (colorArray.length === 4 && colorArray[3] < 0.7) {
            colorArray[3] = 0.85; // Boost alpha for visibility
          }

          // For dot density, create SimpleMarkerSymbol regardless of existing symbol type
          const baseSize = 8;
          const maxSize = 28;
          const sizeRange = maxSize - baseSize;
          const size = baseSize + (sizeRange * (index / (breaksArray.length - 1 || 1)));

          console.log(`[createGeographyHeatMapLayer] Creating dot density marker ${index}: size=${size}, color=${JSON.stringify(colorArray)}`);

          symbol = new SimpleMarkerSymbol({
            style: "circle",
            color: new Color(colorArray),
            size: size,
            outline: new SimpleLineSymbol({
              color: new Color([50, 50, 50, 0.9]),
              width: 1.5
            })
          });
        } else if (!(symbol instanceof SimpleFillSymbol)) {
          // For heatmap, ensure symbol is a SimpleFillSymbol instance
          symbol = new SimpleFillSymbol({
            color: new Color(colorArray),
            outline: new SimpleLineSymbol({
              color: new Color(breakInfo.symbol?.outline?.color || [255, 255, 255, 0.3]),
              width: breakInfo.symbol?.outline?.width || 0.5
            })
          });
        }

        return {
          minValue: breakInfo.minValue,
          maxValue: breakInfo.maxValue,
          symbol: symbol,
          label: breakInfo.label
        };
      });
    } else {
      // Generate new class breaks using quantile method
      console.log("[createGeographyHeatMapLayer] Generating new classBreakInfos from data");

      const sortedValues = [...enrichedValues].sort((a, b) => a - b);

      // Get unique values to determine optimal number of breaks
      const uniqueValues = [...new Set(sortedValues)];
      const maxBreaks = 7; // Match the heatmap color scheme
      const numBreaks = Math.min(maxBreaks, uniqueValues.length);

      console.log("[createGeographyHeatMapLayer] Data has", uniqueValues.length, "unique values, using", numBreaks, "breaks");

      const classBreaks = [];

      for (let i = 0; i < numBreaks; i++) {
        const quantileIndex = Math.floor((sortedValues.length - 1) * (i + 1) / numBreaks);
        classBreaks.push(sortedValues[quantileIndex]);
      }

      console.log("[createGeographyHeatMapLayer] Class breaks:", classBreaks);

      // Color schemes - low opacity for polygon fills, high opacity for dot markers
      const heatmapColors = [
        [128, 0, 128, 0.35],     // Purple
        [0, 0, 139, 0.20],       // Dark blue
        [135, 206, 235, 0.35],   // Sky blue
        [144, 238, 144, 0.35],   // Light green
        [255, 255, 144, 0.35],   // Light yellow
        [255, 165, 0, 0.15],     // Orange
        [255, 99, 71, 0.15],     // Salmon red
      ];

      // Higher opacity colors for dot density markers (dots need to be visible)
      const dotDensityColors = [
        [128, 0, 128, 0.85],     // Purple
        [0, 0, 139, 0.85],       // Dark blue
        [135, 206, 235, 0.85],   // Sky blue
        [144, 238, 144, 0.85],   // Light green
        [255, 255, 144, 0.85],   // Light yellow
        [255, 165, 0, 0.85],     // Orange
        [255, 99, 71, 0.85],     // Salmon red
      ];

      // Helper function to format numbers with decimal places, optional commas, and format type
      const formatNumber = (num) => {
        let formatted;
        if (useCommas) {
          formatted = num.toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
          });
        } else {
          formatted = num.toFixed(decimalPlaces);
        }

        // Apply format type prefix/suffix
        if (numberFormat === 'currency') {
          return '$' + formatted;
        } else if (numberFormat === 'percent') {
          return formatted + '%';
        }
        return formatted;
      };

      classBreakInfos = classBreaks.map((breakValue, index) => {
        // Create different symbols for dot density vs heatmap
        let symbol;
        if (isDotDensity) {
          // For dot density, use circle markers with varying sizes and higher opacity
          const baseSize = 6;
          const maxSize = 24;
          const sizeRange = maxSize - baseSize;
          const size = baseSize + (sizeRange * (index / (numBreaks - 1 || 1)));
          const colorIndex = Math.min(index, dotDensityColors.length - 1);

          symbol = new SimpleMarkerSymbol({
            style: "circle",
            color: new Color(dotDensityColors[colorIndex]),
            size: size,
            outline: new SimpleLineSymbol({
              color: new Color([255, 255, 255, 0.8]),
              width: 1
            })
          });
        } else {
          // For heatmap, use filled polygons with lower opacity
          const colorIndex = Math.min(index, heatmapColors.length - 1);
          symbol = new SimpleFillSymbol({
            color: new Color(heatmapColors[colorIndex]),
            outline: new SimpleLineSymbol({
              color: new Color([255, 255, 255, 0.3]),
              width: 0.5
            })
          });
        }

        // Generate label: "Less than X" for lowest, "X or More" for highest, "X - Y" for middle
        let label;
        if (index === 0) {
          label = `Less than ${formatNumber(breakValue)}`;
        } else if (index === numBreaks - 1) {
          label = `${formatNumber(classBreaks[index - 1])} or More`;
        } else {
          label = `${formatNumber(classBreaks[index - 1])} - ${formatNumber(breakValue)}`;
        }

        return {
          minValue: index === 0 ? -Infinity : classBreaks[index - 1],
          maxValue: breakValue,
          symbol: symbol,
          label: label
        };
      });
    }

    // Helper function to determine which symbol to use based on value
    const getSymbolForValue = (value) => {
      for (let i = 0; i < classBreakInfos.length; i++) {
        const breakInfo = classBreakInfos[i];
        // Handle edge case where minValue == maxValue (single value break)
        if (breakInfo.minValue === breakInfo.maxValue) {
          if (value === breakInfo.minValue) {
            return breakInfo.symbol;
          }
        } else if (value >= breakInfo.minValue && value <= breakInfo.maxValue) {
          return breakInfo.symbol;
        }
      }
      // Return the last symbol as default if no match found
      if (classBreakInfos.length > 0) {
        return classBreakInfos[classBreakInfos.length - 1].symbol;
      }

      // Create default symbol based on visualization type
      if (isDotDensity) {
        return new SimpleMarkerSymbol({
          style: "circle",
          color: new Color([128, 128, 128, 0.6]),
          size: 8,
          outline: new SimpleLineSymbol({
            color: new Color([255, 255, 255, 0.8]),
            width: 1
          })
        });
      } else {
        return new SimpleFillSymbol({
          color: new Color([128, 128, 128, 0.2]),
          outline: new SimpleLineSymbol({
            color: new Color([255, 255, 255, 0.3]),
            width: 0.5
          })
        });
      }
    };

    // Create graphics with symbols applied directly
    // Filter to only features that have matching enrichment data when not using feature service data
    const featuresToRender = hasFieldInFeatures
      ? queryResult.features
      : queryResult.features.filter((_, index) => index < enrichedFeatures.length && enrichedFeatures[index]);

    console.log(`[createGeographyHeatMapLayer] Rendering ${featuresToRender.length} of ${queryResult.features.length} features (hasFieldInFeatures: ${hasFieldInFeatures})`);

    const graphics = featuresToRender.map((feature, index) => {
      let value, attrs;

      if (hasFieldInFeatures) {
        // Use data from feature service
        value = feature.attributes[field] || enrichedValues[index] || 0;
        attrs = feature.attributes;
      } else {
        // Use enrichment results
        const result = enrichedFeatures[index];
        // Add null check to handle cases where enrichment returned fewer results than features
        const enrichedAttrs = result?.value?.FeatureSet?.[0]?.features?.[0]?.attributes || {};
        value = enrichedAttrs[field] || enrichedValues[index] || 0;
        attrs = { ...feature.attributes, ...enrichedAttrs };
      }

      // Determine the appropriate symbol based on the value
      const symbol = getSymbolForValue(value);

      const identifier = feature.attributes[queryField] ||
                        feature.attributes.NAME ||
                        `Area ${index}`;

      // For dot density, use the centroid; for heatmap, use the polygon
      let geometry;
      if (isDotDensity) {
        // Calculate centroid of the polygon
        const polygon = new Polygon({
          rings: feature.geometry.rings,
          spatialReference: feature.geometry.spatialReference || { wkid: 4326 }
        });
        const centroid = polygon.centroid;
        geometry = new Point({
          x: centroid.x,
          y: centroid.y,
          spatialReference: polygon.spatialReference
        });
        if (index === 0) {
          console.log(`[createGeographyHeatMapLayer] DOT DENSITY: Created Point geometry at centroid:`, {
            x: centroid.x,
            y: centroid.y,
            sr: polygon.spatialReference?.wkid,
            symbolType: symbol?.type,
            symbolStyle: symbol?.style,
            symbolSize: symbol?.size,
            symbolColor: symbol?.color?.toRgba ? symbol.color.toRgba() : symbol?.color
          });
        }
      } else {
        geometry = new Polygon({
          rings: feature.geometry.rings,
          spatialReference: feature.geometry.spatialReference || { wkid: 4326 }
        });
      }

      return new Graphic({
        geometry: geometry,
        attributes: {
          ...attrs,
          [field]: value,
          NAME: identifier,
          OBJECTID: index
        },
        symbol: symbol  // Apply symbol directly to graphic
      });
    });

    const geometryTypes = graphics.map(g => g.geometry?.type).filter((v, i, a) => a.indexOf(v) === i);
    console.log(`[createGeographyHeatMapLayer] Created ${graphics.length} graphics with symbols. Geometry types: ${geometryTypes.join(', ')}`);

    const graphicsLayer = new GraphicsLayer({
      graphics: graphics,
      title: title || `Geography Heat Map - ${field}`,
      popupTemplate: new PopupTemplate({
        title: "{NAME}",
        content: [{
          type: "fields",
          fieldInfos: [{
            fieldName: field,
            label: field,
            format: {
              digitSeparator: true,
              places: 2
            }
          }]
        }]
      })
    });

    graphicsLayer.isVisualizationLayer = true;
    graphicsLayer.visualizationType = isDotDensity ? 'geography-dotdensity' : 'geography-heatmap';
    graphicsLayer.isDotDensity = isDotDensity;

    // Store legend info on the layer for custom legend display
    graphicsLayer.legendInfo = {
      field: field,
      classBreakInfos: classBreakInfos,
      title: title || `Geography Heat Map - ${field}`,
      isDotDensity: isDotDensity
    };

    // Store serializable enriched features for caching (geometry + attributes only)
    // This allows fast layer recreation without re-enrichment
    graphicsLayer.enrichedFeatures = graphics.map(g => ({
      geometry: {
        type: g.geometry.type,
        rings: g.geometry.rings || null,  // For polygons
        x: g.geometry.x || null,          // For points
        y: g.geometry.y || null,
        spatialReference: g.geometry.spatialReference?.toJSON?.() || g.geometry.spatialReference || { wkid: 4326 }
      },
      attributes: { ...g.attributes },
      symbolIndex: classBreakInfos.findIndex(b => b.minValue <= g.attributes[field] && g.attributes[field] <= b.maxValue)
    }));

    console.log(`[createGeographyHeatMapLayer] ${isDotDensity ? 'Dot density' : 'Heat map'} layer created successfully with ${graphics.length} graphics, cached for future loads`);

    return graphicsLayer;

  } catch (error) {
    console.error("[createGeographyHeatMapLayer] Error creating geography heat map:", {
      error: error,
      message: error.message,
      details: error.details,
      name: error.name,
      stack: error.stack
    });

    // Log additional error information if available
    if (error.details) {
      console.error("[createGeographyHeatMapLayer] Error details:", JSON.stringify(error.details, null, 2));
    }

    return null;
  }
}

/**
 * Creates a value-based geography heat map layer using pre-supplied values
 * No enrichment is performed - values are provided in the configuration
 *
 * @param {Object} config - Configuration object containing:
 *   - geographyList: Array of area identifiers (e.g., ["90210", "90211"])
 *   - geographyValues: Object mapping areas to values (e.g., {"90210": 1234, "90211": 5678})
 *   - areaType: Type of geography (e.g., "zip", "county", "place")
 *   - field: Field name for the value (defaults to "Value")
 * @param {Object} selectedAreaType - Area type configuration with URL
 * @returns {Promise<GraphicsLayer>} Graphics layer with geography polygons colored by value
 */
async function createGeographyValueHeatMapLayer(config, selectedAreaType) {
  console.log("[createGeographyValueHeatMapLayer] Creating value-based geography heat map with config:", config);

  try {
    const { geographyList, geographyValues, areaType, field = 'Value', title, decimalPlaces = 0, useCommas = true, numberFormat = 'number' } = config;

    console.log("[createGeographyValueHeatMapLayer] DEBUG - Checking inputs:", {
      hasGeographyList: !!geographyList,
      isArray: Array.isArray(geographyList),
      length: geographyList?.length,
      hasGeographyValues: !!geographyValues,
      valuesCount: geographyValues ? Object.keys(geographyValues).length : 0,
      areaType,
      field,
      hasSelectedAreaType: !!selectedAreaType,
      selectedAreaTypeUrl: selectedAreaType?.url
    });

    if (!geographyList || !Array.isArray(geographyList) || geographyList.length === 0) {
      console.error("[createGeographyValueHeatMapLayer] EARLY RETURN 1: No geography list provided");
      return null;
    }

    if (!geographyValues || Object.keys(geographyValues).length === 0) {
      console.error("[createGeographyValueHeatMapLayer] EARLY RETURN 2: No geography values provided");
      return null;
    }

    if (!selectedAreaType?.url) {
      console.error("[createGeographyValueHeatMapLayer] EARLY RETURN 3: No area type URL provided");
      return null;
    }

    console.log(`[createGeographyValueHeatMapLayer] Geocoding ${geographyList.length} ${areaType} areas with provided values`);

    // Create a temporary FeatureLayer to query field information
    const tempLayer = new FeatureLayer({
      url: selectedAreaType.url
    });

    // Load the layer to access its fields
    await tempLayer.load();
    console.log(`[createGeographyValueHeatMapLayer] Loaded feature layer, available fields:`,
      tempLayer.fields.map(f => f.name).join(', '));

    // Map area type to potential field names (in priority order)
    const areaFieldCandidates = {
      'zip': ['ID', 'ZIP_CODE', 'ZIPCODE', 'ZIP', 'ZCTA5', 'ZCTA', 'GEOID', 'NAME'],
      'county': ['ID', 'NAME', 'COUNTY_NAME', 'COUNTYNAME', 'GEOID'],
      'place': ['ID', 'NAME', 'PLACE_NAME', 'PLACENAME', 'GEOID'],
      'tract': ['ID', 'GEOID', 'TRACT', 'NAME'],
      'block_group': ['ID', 'GEOID', 'BLKGRP', 'NAME'],
      'cbsa': ['ID', 'NAME', 'CBSA_NAME', 'CBSANAME', 'GEOID'],
      'state': ['ID', 'NAME', 'STATE_NAME', 'STATENAME', 'STUSPS', 'GEOID']
    };

    // Find the correct field by checking which candidate exists in the layer
    const candidates = areaFieldCandidates[areaType] || ['NAME'];
    const layerFieldNames = tempLayer.fields.map(f => f.name.toUpperCase());

    let queryField = null;
    for (const candidate of candidates) {
      if (layerFieldNames.includes(candidate.toUpperCase())) {
        queryField = tempLayer.fields.find(f => f.name.toUpperCase() === candidate.toUpperCase()).name;
        break;
      }
    }

    if (!queryField) {
      console.error(`[createGeographyValueHeatMapLayer] EARLY RETURN 4: Could not find suitable field for ${areaType}. Available fields:`, layerFieldNames);
      return null;
    }

    console.log(`[createGeographyValueHeatMapLayer] Using field ${queryField} for querying ${areaType} areas`);

    // First, do a sample query to verify field content
    try {
      const sampleQuery = new Query({
        where: '1=1',
        outFields: [queryField, 'NAME'],
        returnGeometry: false,
        num: 5
      });

      console.log(`[createGeographyValueHeatMapLayer] Executing sample query...`);
      const sampleResult = await tempLayer.queryFeatures(sampleQuery);
      if (sampleResult.features && sampleResult.features.length > 0) {
        console.log(`[createGeographyValueHeatMapLayer] Sample ${queryField} values from feature service:`,
          sampleResult.features.map(f => ({
            [queryField]: f.attributes[queryField],
            NAME: f.attributes.NAME,
            type: typeof f.attributes[queryField]
          }))
        );
      } else {
        console.warn(`[createGeographyValueHeatMapLayer] Sample query returned no features`);
      }
    } catch (sampleError) {
      console.error(`[createGeographyValueHeatMapLayer] Sample query failed:`, sampleError);
    }

    // Query the feature service to get geometries for the specific areas
    console.log("[createGeographyValueHeatMapLayer] Geography list being queried:", geographyList.map(area => ({
      value: area,
      type: typeof area,
      length: area?.length
    })));

    const whereClause = `${queryField} IN (${geographyList.map(area => `'${area}'`).join(', ')})`;

    const queryObj = new Query({
      where: whereClause,
      outFields: ['*'],
      returnGeometry: true,
      outSpatialReference: { wkid: 4326 }
    });

    console.log("[createGeographyValueHeatMapLayer] Querying feature service:", {
      url: selectedAreaType.url,
      where: queryObj.where,
      field: queryField,
      areaType: areaType
    });

    const queryResult = await tempLayer.queryFeatures(queryObj);

    if (!queryResult.features || queryResult.features.length === 0) {
      console.warn("[createGeographyValueHeatMapLayer] No features found for the specified areas");
      return null;
    }

    console.log(`[createGeographyValueHeatMapLayer] Found ${queryResult.features.length} features`);

    // Extract values for the found features
    const enrichedValues = [];
    const enrichedFeatures = [];

    queryResult.features.forEach((feature) => {
      const identifier = feature.attributes[queryField] ||
                        feature.attributes.NAME ||
                        feature.attributes.GEOID;

      // Look up the value from geographyValues
      // Try both the identifier as-is and as a string (in case of number vs string mismatch)
      let value = geographyValues[identifier];
      if (value === undefined) {
        value = geographyValues[String(identifier)];
      }

      if (value !== undefined && value !== null && !isNaN(parseFloat(value))) {
        enrichedValues.push(parseFloat(value));
        enrichedFeatures.push({
          feature: feature,
          identifier: identifier,
          value: parseFloat(value)
        });
        console.log(`[createGeographyValueHeatMapLayer] ${identifier} (type: ${typeof identifier}): ${value}`);
      } else {
        console.warn(`[createGeographyValueHeatMapLayer] No value found for ${identifier} (type: ${typeof identifier})`);
      }
    });

    if (enrichedValues.length === 0) {
      console.error("[createGeographyValueHeatMapLayer] No valid values found");
      return null;
    }

    console.log("[createGeographyValueHeatMapLayer] Values:", enrichedValues);

    let classBreakInfos;

    // Check if classBreakInfos already exist in config (from user edits)
    // First, extract the actual breaks array (handle nested structure if present)
    let breaksArray = null;
    if (config.classBreakInfos) {
      if (Array.isArray(config.classBreakInfos)) {
        breaksArray = config.classBreakInfos;
      } else if (config.classBreakInfos.classBreakInfos && Array.isArray(config.classBreakInfos.classBreakInfos)) {
        console.log("[createGeographyValueHeatMapLayer] Found nested classBreakInfos structure, extracting array");
        breaksArray = config.classBreakInfos.classBreakInfos;
      }
    }

    if (breaksArray && breaksArray.length > 0) {
      console.log("[createGeographyValueHeatMapLayer] Using existing classBreakInfos from config:", breaksArray.length, "breaks");

      // Use existing classBreakInfos but ensure symbols are properly formatted
      classBreakInfos = breaksArray.map(breakInfo => {
        // Ensure symbol is a SimpleFillSymbol instance
        let symbol = breakInfo.symbol;
        if (!(symbol instanceof SimpleFillSymbol)) {
          // Convert plain object to SimpleFillSymbol
          const colorArray = Array.isArray(breakInfo.symbol?.color)
            ? breakInfo.symbol.color
            : [128, 0, 128, 0.35];

          symbol = new SimpleFillSymbol({
            color: new Color(colorArray),
            outline: new SimpleLineSymbol({
              color: new Color(breakInfo.symbol?.outline?.color || [255, 255, 255, 0.3]),
              width: breakInfo.symbol?.outline?.width || 0.5
            })
          });
        }

        return {
          minValue: breakInfo.minValue,
          maxValue: breakInfo.maxValue,
          symbol: symbol,
          label: breakInfo.label
        };
      });
    } else {
      // Generate new class breaks using quantile method
      console.log("[createGeographyValueHeatMapLayer] Generating new classBreakInfos from data");

      const sortedValues = [...enrichedValues].sort((a, b) => a - b);

      // Get unique values to determine optimal number of breaks
      const uniqueValues = [...new Set(sortedValues)];
      const maxBreaks = 7; // Match heatmap color scheme
      const numBreaks = Math.min(maxBreaks, uniqueValues.length);

      console.log("[createGeographyValueHeatMapLayer] Data has", uniqueValues.length, "unique values, using", numBreaks, "breaks");

      const classBreaks = [];

      for (let i = 0; i < numBreaks; i++) {
        const quantileIndex = Math.floor((sortedValues.length - 1) * (i + 1) / numBreaks);
        classBreaks.push(sortedValues[quantileIndex]);
      }

      console.log("[createGeographyValueHeatMapLayer] Class breaks:", classBreaks);

      // Use same color scheme as heatmaps (from heatMapGenerator.js)
      const heatmapColors = [
        [128, 0, 128, 0.35],     // Purple
        [0, 0, 139, 0.20],       // Dark blue
        [135, 206, 235, 0.35],   // Sky blue
        [144, 238, 144, 0.35],   // Light green
        [255, 255, 144, 0.35],   // Light yellow
        [255, 165, 0, 0.15],     // Orange
        [255, 99, 71, 0.15],     // Salmon red
      ];

      // Helper function to format numbers with decimal places, optional commas, and format type
      const formatNumber = (num) => {
        let formatted;
        if (useCommas) {
          formatted = num.toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
          });
        } else {
          formatted = num.toFixed(decimalPlaces);
        }

        // Apply format type prefix/suffix
        if (numberFormat === 'currency') {
          return '$' + formatted;
        } else if (numberFormat === 'percent') {
          return formatted + '%';
        }
        return formatted;
      };

      classBreakInfos = classBreaks.map((breakValue, index) => {
        // Generate label: "Less than X" for lowest, "X or More" for highest, "X - Y" for middle
        let label;
        if (index === 0) {
          label = `Less than ${formatNumber(breakValue)}`;
        } else if (index === numBreaks - 1) {
          label = `${formatNumber(classBreaks[index - 1])} or More`;
        } else {
          label = `${formatNumber(classBreaks[index - 1])} - ${formatNumber(breakValue)}`;
        }

        return {
          minValue: index === 0 ? -Infinity : classBreaks[index - 1],
          maxValue: breakValue,
          symbol: new SimpleFillSymbol({
            color: new Color(heatmapColors[index]),
            outline: new SimpleLineSymbol({
              color: new Color([255, 255, 255, 0.3]),
              width: 0.5
            })
          }),
          label: label
        };
      });
    }

    // Helper function to determine which symbol to use based on value
    const getSymbolForValue = (value) => {
      for (let i = 0; i < classBreakInfos.length; i++) {
        const breakInfo = classBreakInfos[i];
        // Handle edge case where minValue == maxValue (single value break)
        if (breakInfo.minValue === breakInfo.maxValue) {
          if (value === breakInfo.minValue) {
            return breakInfo.symbol;
          }
        } else if (value >= breakInfo.minValue && value <= breakInfo.maxValue) {
          return breakInfo.symbol;
        }
      }
      // Return the last symbol as default if no match found
      return classBreakInfos.length > 0
        ? classBreakInfos[classBreakInfos.length - 1].symbol
        : new SimpleFillSymbol({
            color: new Color([128, 128, 128, 0.2]),
            outline: new SimpleLineSymbol({
              color: new Color([255, 255, 255, 0.3]),
              width: 0.5
            })
          });
    };

    // Create graphics with proper symbols
    const graphics = enrichedFeatures.map(({ feature, identifier, value }, index) => {
      const symbol = getSymbolForValue(value);

      return new Graphic({
        geometry: feature.geometry,
        attributes: {
          [field]: value,
          NAME: identifier,
          OBJECTID: index
        },
        symbol: symbol
      });
    });

    console.log(`[createGeographyValueHeatMapLayer] Created ${graphics.length} graphics with symbols`);

    const graphicsLayer = new GraphicsLayer({
      graphics: graphics,
      title: title || `Geography Map - ${field}`,
      popupTemplate: new PopupTemplate({
        title: "{NAME}",
        content: [{
          type: "fields",
          fieldInfos: [{
            fieldName: field,
            label: field,
            format: {
              digitSeparator: true,
              places: 2
            }
          }]
        }]
      })
    });

    graphicsLayer.isVisualizationLayer = true;
    graphicsLayer.visualizationType = 'geography-value';

    // Store legend info on the layer for custom legend display
    graphicsLayer.legendInfo = {
      field: field,
      classBreakInfos: classBreakInfos,
      title: title || `Geography Map - ${field}`
    };

    console.log("[createGeographyValueHeatMapLayer] Geography value heat map layer created successfully");

    return graphicsLayer;

  } catch (error) {
    console.error("[createGeographyValueHeatMapLayer] Error creating geography value heat map:", {
      error: error,
      message: error.message,
      details: error.details,
      name: error.name,
      stack: error.stack
    });

    return null;
  }
}

/**
 * Creates and returns an appropriate layer based on visualization type
 * Handles both standard feature layers and custom graphics layers
 *
 * @param {string} visualizationType - Type of visualization to create
 * @param {Object} configOverride - Configuration overrides for the layer
 * @param {Object} layerConfigs - Base layer configurations
 * @param {Object} selectedAreaType - Selected area type object
 * @returns {Promise<FeatureLayer|GraphicsLayer>} The created layer
 */
export async function createLayers(
    visualizationType,
    configOverride = null,
    layerConfigs = {}, 
    selectedAreaType = null
) {
    // Input validation and configuration setup
    if (!visualizationType && !configOverride?.type) {
        console.error("[createLayers] No visualization type or config type provided.");
        return null;
    }

    const baseConfig = layerConfigs[visualizationType];
    let config = configOverride || baseConfig;

    if (!config) {
        console.warn(`[createLayers] No specific config found for '${visualizationType}'. Using minimal defaults based on type.`);
        // Create a minimal structure, specific creators will handle defaults
        config = { type: visualizationType };
    } else if (configOverride) {
        console.log("[createLayers] Using provided config override");
        config = { ...configOverride }; // Clone override
    } else {
        console.log(`[createLayers] Using initial config for '${visualizationType}'`);
        config = { ...baseConfig }; // Clone base config
    }

    // Ensure config always has a type property
    if (!config.type) {
        config.type = visualizationType;
    }

    // Check for geography types FIRST before any normalization
    const geographyTypes = ['geography', 'geography-heatmap', 'geography-value', 'geography-dotdensity'];
    let effectiveVizType = config.type;

    // Also check for isDotDensity flag - during editing/preview the type may become "dot-density"
    // but the isDotDensity flag indicates it should be routed to geography-dotdensity
    const isGeographyDotDensity = config.isDotDensity && config.attributes?.length > 0;

    if (geographyTypes.includes(effectiveVizType) || isGeographyDotDensity) {
        // Force type to geography-dotdensity if isDotDensity flag is set
        if (isGeographyDotDensity && !geographyTypes.includes(effectiveVizType)) {
            effectiveVizType = 'geography-dotdensity';
            config.type = 'geography-dotdensity';
            console.log(`[createLayers] isDotDensity flag detected, forcing type to geography-dotdensity`);
        }
        console.log(`[createLayers] Geography type detected: ${effectiveVizType}, skipping normalization`);
    } else {
        // Normalize visualization type using the enhanced normalizeRendererType function
        effectiveVizType = normalizeRendererType(config.type, config) || config.type;

        // Handle legacy type normalization for special graphics layer types
        if (effectiveVizType === "pipeline") effectiveVizType = "pipe";
        if (effectiveVizType === "comps") effectiveVizType = "comp";

        // Handle _HEAT field suffix normalization
        if (effectiveVizType && effectiveVizType.endsWith('_HEAT')) {
            console.log(`[createLayers] Normalizing HEAT type ${effectiveVizType} to class-breaks`);
            effectiveVizType = 'class-breaks';
            config.type = 'class-breaks';
        }

        // Use base config type if still ambiguous
        if (baseConfig && baseConfig.type &&
            !['pipe', 'comp', 'custom', 'class-breaks', 'dot-density', 'geography-heatmap'].includes(effectiveVizType)) {
            console.log(`[createLayers] Using base config type '${baseConfig.type}' for ambiguous type '${effectiveVizType}'`);
            effectiveVizType = baseConfig.type;
            config.type = effectiveVizType;
        }
    }

    console.log(`[createLayers] Effective visualization type determined as: ${effectiveVizType}`);

    // Handle special GraphicsLayer types
    const specialTypes = ["pipe", "comp", "custom", "geography", "geography-heatmap", "geography-value", "geography-dotdensity"];
    const hasCustomData = config.customData && Array.isArray(config.customData.data) && config.customData.data.length > 0;

    if (specialTypes.includes(effectiveVizType)) {
        console.log(`[createLayers] Handling special type: ${effectiveVizType}`);
        let graphicsLayer = null;

        try {
            if (effectiveVizType === "pipe") {
                graphicsLayer = createPipeLayer(config);
            } else if (effectiveVizType === "comp") {
                graphicsLayer = createCompLayer(config);
            } else if (effectiveVizType === "custom") {
                graphicsLayer = await createGraphicsLayerFromCustomData(config);
            } else if (effectiveVizType === "geography-dotdensity") {
                // Dot density maps - route directly to dot density layer creator
                console.log(`[createLayers] Creating geography dot density layer`);
                graphicsLayer = await createGeographyDotDensityLayer(config, selectedAreaType);
            } else if (effectiveVizType === "geography-heatmap" || effectiveVizType === "geography" || effectiveVizType === "geography-value") {
                // Check if it's actually a value-based map (has geographyValues) or enrichment-based
                if (config.geographyValues && Object.keys(config.geographyValues).length > 0) {
                    console.log(`[createLayers] Using value-based geography layer (has geographyValues)`);
                    graphicsLayer = await createGeographyValueHeatMapLayer(config, selectedAreaType);
                } else {
                    console.log(`[createLayers] Using enrichment-based geography layer (no geographyValues)`);
                    graphicsLayer = await createGeographyHeatMapLayer(config, selectedAreaType);
                }
            }

            // Check for both GraphicsLayer and FeatureLayer (dot density returns FeatureLayer)
            if (graphicsLayer instanceof GraphicsLayer || graphicsLayer instanceof FeatureLayer) {
                graphicsLayer.isVisualizationLayer = true;
                // Preserve the visualizationType set by the layer creator (e.g., geography-dotdensity)
                if (!graphicsLayer.visualizationType) {
                    graphicsLayer.visualizationType = effectiveVizType;
                }
                const layerType = graphicsLayer instanceof FeatureLayer ? 'FeatureLayer' : 'GraphicsLayer';
                console.log(`[createLayers] Successfully created ${layerType}: "${graphicsLayer.title}", type: "${graphicsLayer.visualizationType}"`);
                return graphicsLayer;
            } else {
                console.error(`[createLayers] Failed to create a valid layer for type ${effectiveVizType}`);
                return null;
            }
        } catch (error) {
            console.error(`[createLayers] Error creating GraphicsLayer for ${effectiveVizType}:`, error);
            return null;
        }
    }
    // Fallback for custom data with non-standard type
    else if (hasCustomData && !['class-breaks', 'dot-density'].includes(effectiveVizType)) {
        console.log(`[createLayers] Handling as 'custom' based on customData presence and non-standard type '${effectiveVizType}'`);
        try {
            config.type = 'custom';
            const graphicsLayer = await createGraphicsLayerFromCustomData(config);
            if (graphicsLayer instanceof GraphicsLayer) {
                graphicsLayer.isVisualizationLayer = true;
                graphicsLayer.visualizationType = 'custom';
                console.log(`[createLayers] Successfully created fallback custom GraphicsLayer: "${graphicsLayer.title}"`);
                return graphicsLayer;
            } else {
                console.error(`[createLayers] Failed to create fallback custom GraphicsLayer`);
                return null;
            }
        } catch (error) {
            console.error(`[createLayers] Error creating fallback custom GraphicsLayer:`, error);
            return null;
        }
    }

    // Standard FeatureLayer implementation for heatmap/dot density
    console.log(`[createLayers] Handling as standard type: ${effectiveVizType}`);
    
    if (!selectedAreaType?.url) {
        console.error(`[createLayers] Invalid area type for standard visualization '${effectiveVizType}': No URL.`, selectedAreaType);
        return null;
    }

    try {
        // Create renderer for the feature layer using the enhanced createRenderer function
        const renderer = await createRenderer(config, selectedAreaType);
        if (!renderer) {
            console.error(`[createLayers] Failed to create renderer for ${effectiveVizType}`);
            return null;
        }

        // Create feature layer with proper configuration
        const featureLayer = new FeatureLayer({
            url: selectedAreaType.url,
            renderer: renderer,
            popupTemplate: createPopupTemplate(effectiveVizType, config, selectedAreaType),
            title: config.title || effectiveVizType,
            minScale: selectedAreaType.value === 12 ? 2500000 : 25000000,
            outFields: ["*", "NAME"],
            definitionExpression: config.definitionExpression || null
        });

        // Set visualization flags
        featureLayer.isVisualizationLayer = true;
        featureLayer.visualizationType = effectiveVizType;

        console.log(`[createLayers] FeatureLayer created: "${featureLayer.title}", type: "${featureLayer.visualizationType}"`);
        return featureLayer;
    } catch (error) {
        console.error(`[createLayers] Failed to create FeatureLayer:`, error);
        return null;
    }
}

/**
 * Create a popup template for feature layers
 * 
 * @param {string} vizType - Visualization type
 * @param {Object} config - Layer configuration
 * @param {Object} selectedAreaType - Area type information
 * @returns {Object} The configured popup template
 */
function createPopupTemplate(vizType, config, selectedAreaType) {
    // Get field and formatting information
    const label = config?.attributes?.[0]?.label || config?.label || config?.field || vizType.replace(/_/g, ' ');
    const field = config?.field;
    const format = config?.valueFormat || { 
        digitSeparator: true, 
        places: (config?.type === 'class-breaks' ? 1 : 0) 
    };

    if (!field) {
        return {
            title: `${selectedAreaType.label} {NAME}`,
            content: "No specific data field configured for popup."
        };
    }

    return {
        title: `${selectedAreaType.label} {NAME}`,
        content: [{
            type: "fields",
            fieldInfos: [{
                fieldName: field,
                label: label,
                format: format
            }]
        }]
    };
}

/**
 * Factory function to update label visibility based on zoom level
 * 
 * @param {MapView} view - The ArcGIS MapView
 * @param {GraphicsLayer} layer - The graphics layer containing labels
 */
export function setupLabelVisibilityHandling(view, layer) {
  if (!view || !layer || !layer.labelVisibilityTracking?.enabled) return;
  
  const tracking = layer.labelVisibilityTracking;
  const minZoom = tracking.minimumZoom || 10;
  
  // Watch for zoom changes
  const zoomHandle = view.watch("zoom", (newZoom) => {
    // Determine if labels should be visible at this zoom level
    const shouldBeVisible = newZoom >= minZoom;
    
    // Skip if visibility state hasn't changed
    if (tracking.currentlyVisible === shouldBeVisible) return;
    
    // Update the tracking state
    tracking.currentlyVisible = shouldBeVisible;
    
    // Find and update all label graphics
    layer.graphics.forEach(graphic => {
      const attrs = graphic.attributes;
      if (attrs && attrs.isLabel === true) {
        // Only update if the base visibility allows it (some labels might be hidden due to collision)
        if (attrs.visible !== false) {
          graphic.visible = shouldBeVisible;
        }
      }
    });
    
    console.log(`[LabelVisibility] ${layer.title}: Labels ${shouldBeVisible ? 'shown' : 'hidden'} at zoom level ${newZoom.toFixed(2)}`);
  });
  
  // Store the handle for cleanup
  layer.labelVisibilityHandle = zoomHandle;
  
  // Initial update
  const currentZoom = view.zoom;
  tracking.currentlyVisible = currentZoom >= minZoom;
  
  // Apply initial visibility
  layer.graphics.forEach(graphic => {
    const attrs = graphic.attributes;
    if (attrs && attrs.isLabel === true) {
      if (attrs.visible !== false) {
        graphic.visible = tracking.currentlyVisible;
      }
    }
  });
}

/**
 * Cleanup function to remove zoom watch handlers
 * 
 * @param {GraphicsLayer} layer - The layer to clean up
 */
export function cleanupLabelVisibilityHandling(layer) {
  if (layer && layer.labelVisibilityHandle) {
    layer.labelVisibilityHandle.remove();
    layer.labelVisibilityHandle = null;
  }
}

// ===== NEW COORDINATE-BASED LABEL SYSTEM =====

/**
 * Create a coordinate-based label manager for a layer
 * This replaces pixel-based offsets with geographic coordinates
 * 
 * @param {Object} view - ArcGIS MapView instance
 * @param {Object} mapConfiguration - Map configuration object
 * @param {Object} options - Label manager options
 * @returns {CoordinateLabelManager} - New label manager instance
 */
export function createCoordinateLabelManager(view, mapConfiguration, options = {}) {
  console.log("[mapLayerUtils] Creating coordinate-based label manager");
  
  const defaultOptions = {
    enableCollisionDetection: true,
    collisionPadding: 3.5, // Reduced by 30% to place labels closer to data points
    defaultOffset: 20, // meters
    ...options
  };
  
  return new CoordinateLabelManager(view, mapConfiguration, defaultOptions);
}

/**
 * Create coordinate-based labels for a dataset
 * This function replaces the old pixel-offset label creation
 * 
 * @param {Object} config - Configuration object
 * @param {Array} config.data - Array of data points
 * @param {string} config.labelColumn - Column to use for label text
 * @param {CoordinateLabelManager} config.labelManager - Label manager instance
 * @param {Object} config.style - Label styling options
 * @returns {Array} - Array of created label graphics
 */
export function createCoordinateBasedLabels(config) {
  const {
    data,
    labelColumn,
    labelManager,
    style = {},
    variable1Column,
    variable2Column,
    variable1Text = '',
    variable2Text = '',
    variable1Prefix = '',
    variable2Prefix = ''
  } = config;
  
  if (!data || !labelManager || !labelColumn) {
    console.warn("[mapLayerUtils] Missing required parameters for coordinate-based labels");
    return [];
  }
  
  console.log(`[mapLayerUtils] Creating ${data.length} coordinate-based labels`);
  
  const createdLabels = [];
  
  data.forEach((item, index) => {
    try {
      // Get coordinates
      const latitude = parseFloat(item.latitude || item.lat || item.Latitude || item.LAT);
      const longitude = parseFloat(item.longitude || item.lng || item.lon || item.Longitude || item.LON);
      
      if (isNaN(latitude) || isNaN(longitude)) {
        console.warn(`[mapLayerUtils] Invalid coordinates for item ${index}:`, { latitude, longitude });
        return;
      }
      
      // Build label text with variables
      let labelText = item[labelColumn] || `Point ${index}`;

      if (variable1Column && item[variable1Column] !== undefined) {
        const var1Value = item[variable1Column];
        const var1Display = variable1Text || variable1Column;
        labelText += `\n${variable1Prefix || ''}${var1Display}: ${var1Value}`;
      }

      if (variable2Column && item[variable2Column] !== undefined) {
        const var2Value = item[variable2Column];
        const var2Display = variable2Text || variable2Column;
        labelText += `\n${variable2Prefix || ''}${var2Display}: ${var2Value}`;
      }
      
      // Create label using coordinate manager
      const labelId = `label-${index}`;
      const labelGraphic = labelManager.createLabel({
        labelId,
        latitude,
        longitude,
        text: labelText,
        markerSize: item.markerSize || 12, // Pass marker size from item attributes for collision boundary scaling
        style: {
          fontSize: style.fontSize || 12,
          color: style.color || "#000000",
          haloColor: style.haloColor || "#FFFFFF",
          haloSize: style.haloSize || 1,
          fontFamily: style.fontFamily || "sans-serif",
          fontWeight: style.fontWeight || "normal"
        },
        attributes: {
          ...item,
          originalIndex: index,
          labelColumn,
          variable1Column,
          variable2Column,
          variable1Text,
          variable2Text,
          variable1Prefix,
          variable2Prefix
        }
      });
      
      if (labelGraphic) {
        createdLabels.push(labelGraphic);
      }
      
    } catch (error) {
      console.error(`[mapLayerUtils] Error creating coordinate label for item ${index}:`, error);
    }
  });
  
  console.log(`[mapLayerUtils] Successfully created ${createdLabels.length} coordinate-based labels`);
  return createdLabels;
}

/**
 * Update existing layer to use coordinate-based labels
 * Migrates from pixel offsets to coordinate positioning
 * 
 * @param {GraphicsLayer} layer - Existing graphics layer
 * @param {Object} view - ArcGIS MapView instance
 * @param {Object} mapConfiguration - Map configuration
 * @returns {Promise<CoordinateLabelManager|null>} - Label manager if successful
 */
export async function upgradeLayerToCoordinateLabels(layer, view, mapConfiguration) {
  if (!layer || !view) {
    console.warn("[mapLayerUtils] Missing layer or view for coordinate label upgrade");
    return null;
  }
  
  console.log("[mapLayerUtils] Upgrading layer to coordinate-based labels");
  
  try {
    // Create new label manager
    const labelManager = createCoordinateLabelManager(view, mapConfiguration);
    
    // Find existing label graphics
    const existingLabels = layer.graphics.filter(graphic => 
      graphic.attributes?.isLabel === true ||
      graphic.symbol?.type === "text"
    );
    
    console.log(`[mapLayerUtils] Found ${existingLabels.length} existing labels to upgrade`);
    
    // Convert existing labels to coordinate-based
    existingLabels.forEach((labelGraphic, index) => {
      try {
        const geometry = labelGraphic.geometry;
        if (!geometry || !geometry.latitude || !geometry.longitude) {
          console.warn(`[mapLayerUtils] Label ${index} missing valid coordinates`);
          return;
        }
        
        const labelId = labelGraphic.attributes?.labelId || `upgraded-label-${index}`;
        const text = labelGraphic.symbol?.text || labelGraphic.attributes?.text || "";
        
        // Create new coordinate-based label
        const newLabel = labelManager.createLabel({
          labelId,
          latitude: geometry.latitude,
          longitude: geometry.longitude,
          text,
          markerSize: labelGraphic.attributes?.markerSize || 12, // Pass marker size for collision boundary scaling
          style: {
            fontSize: labelGraphic.symbol?.font?.size || 12,
            color: labelGraphic.symbol?.color?.toHex() || "#000000",
            haloColor: labelGraphic.symbol?.haloColor?.toHex() || "#FFFFFF",
            haloSize: labelGraphic.symbol?.haloSize || 1,
            fontFamily: labelGraphic.symbol?.font?.family || "sans-serif",
            fontWeight: labelGraphic.symbol?.font?.weight || "normal"
          },
          attributes: labelGraphic.attributes
        });
        
        // Remove old label from layer
        layer.remove(labelGraphic);
        
      } catch (error) {
        console.error(`[mapLayerUtils] Error upgrading label ${index}:`, error);
      }
    });
    
    // Add new coordinate-based labels to layer
    const newGraphics = labelManager.getAllGraphics();
    layer.addMany(newGraphics);
    
    // Store label manager reference on layer
    layer.coordinateLabelManager = labelManager;
    
    console.log(`[mapLayerUtils] Successfully upgraded ${newGraphics.length} labels to coordinate-based system`);
    return labelManager;
    
  } catch (error) {
    console.error("[mapLayerUtils] Error upgrading layer to coordinate labels:", error);
    return null;
  }
}

/**
 * Create a coordinate-based label dragger for interactive label positioning
 * 
 * @param {Object} view - ArcGIS MapView instance  
 * @param {CoordinateLabelManager} labelManager - Label manager instance
 * @param {Object} callbacks - Optional callbacks for drag events
 * @returns {CoordinateLabelDragger} - New label dragger instance
 */
export function createCoordinateLabelDragger(view, labelManager, callbacks = {}) {
  console.log("[mapLayerUtils] Creating coordinate-based label dragger");
  
  const dragger = new CoordinateLabelDragger({
    view,
    labelManager,
    onDragStart: callbacks.onDragStart,
    onDrag: callbacks.onDrag,
    onDragEnd: callbacks.onDragEnd
  });
  
  return dragger;
}

/**
 * Save coordinate-based label positions to backend
 * 
 * @param {CoordinateLabelManager} labelManager - Label manager instance
 * @param {string} projectId - Project ID
 * @param {string} mapConfigurationId - Map configuration ID
 * @returns {Promise<boolean>} - Success status
 */
export async function saveCoordinateLabelPositions(labelManager, projectId, mapConfigurationId) {
  if (!labelManager || !projectId) {
    console.warn("[mapLayerUtils] Missing required parameters for saving label positions");
    return false;
  }
  
  console.log("[mapLayerUtils] Saving coordinate-based label positions");
  
  try {
    const labelData = [];
    
    // Collect all label positions
    for (const [labelId, label] of labelManager.labels.entries()) {
      labelData.push({
        label_id: labelId,
        latitude: label.latitude,
        longitude: label.longitude,
        text: label.text,
        font_size: label.style.fontSize || 12,
        visibility: true,
        font_weight: label.style.fontWeight || "normal",
        has_background: label.style.hasBackground || false,
        background_color: label.style.backgroundColor || null,
        background_opacity: label.style.backgroundOpacity || 0.85,
        bounding_box_width: labelManager.boundingBoxes.get(labelId)?.width || 0,
        bounding_box_height: labelManager.boundingBoxes.get(labelId)?.height || 0,
        collision_offset_applied: label.collisionOffset || false,
        original_latitude: label.originalLatitude,
        original_longitude: label.originalLongitude
      });
    }
    
    // Save to backend using existing label position API
    const response = await fetch('/api/label-positions/batch_save/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify({
        project_id: projectId,
        map_configuration_id: mapConfigurationId,
        labels: labelData
      })
    });
    
    if (response.ok) {
      console.log(`[mapLayerUtils] Successfully saved ${labelData.length} coordinate-based label positions`);
      return true;
    } else {
      const error = await response.json();
      console.error("[mapLayerUtils] Error saving label positions:", error);
      return false;
    }
    
  } catch (error) {
    console.error("[mapLayerUtils] Error saving coordinate label positions:", error);
    return false;
  }
}

/**
 * Load coordinate-based label positions from backend
 * 
 * @param {string} projectId - Project ID
 * @param {string} mapConfigurationId - Map configuration ID (optional)
 * @returns {Promise<Array>} - Array of label position data
 */
export async function loadCoordinateLabelPositions(projectId, mapConfigurationId = null) {
  if (!projectId) {
    console.warn("[mapLayerUtils] Missing project ID for loading label positions");
    return [];
  }
  
  console.log("[mapLayerUtils] Loading coordinate-based label positions");
  
  try {
    let url = `/api/label-positions/?project=${projectId}`;
    if (mapConfigurationId) {
      url += `&map_configuration=${mapConfigurationId}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`[mapLayerUtils] Loaded ${data.length} coordinate-based label positions`);
      return data;
    } else {
      console.error("[mapLayerUtils] Error loading label positions:", response.status);
      return [];
    }
    
  } catch (error) {
    console.error("[mapLayerUtils] Error loading coordinate label positions:", error);
    return [];
  }
}